import {
	arrayRemove,
	arrayUnion,
	collection,
	deleteDoc,
	deleteField,
	doc,
	getDoc,
	getDocFromCache,
	getDocs,
	getDocsFromCache,
	getDocsFromServer,
	limit,
	getCountFromServer,
	increment,
	onSnapshot,
	orderBy,
	query,
	serverTimestamp,
	setDoc,
	startAfter,
	Timestamp,
	updateDoc,
	where,
	writeBatch,
} from "firebase/firestore"
import { DateTime } from "luxon"
import { firestoreDB } from "../services/firebase.service"
import { trackSync } from "../services/sync.service"
import { isNetworkError, isOffline, requireNetwork } from "./network.utils"
import {
	createCalendarEvent,
	editCalendarEvent,
	deleteCalendarEvent,
	isReconnectError,
	isNotConnectedError,
} from "../providers/profile.provider"
import { queueCalendarRepair } from "../services/calendarQueue.service"
import { buildEventTitle, calendarAvailable, calendarEventID } from "./calendar.utils"
import { toast } from "react-toastify"
import { BILLED_TO_PLAYERS, COURT_PAID_BY_COACH, isSubscriptionCourse } from "./billing.utils"
import {
	buildEventLocation,
	getPlayerFullName,
	findKnownPlayer,
	normalizeName,
	hasLessonPayment,
	reconcileLessonPayments,
	splitTypedPlayerName,
	normalizeSchoolIDs,
	withLessonTypeDefaults,
	isPlaceholderPartecipant,
} from "./func.utils"
import {
	getSeasonBounds,
	isSubstitute,
	lessonTracksRecovery,
	keptSubstitutes,
	lessonSubstitutes,
	recoveriesLeft,
	regularPartecipants,
	setRecoveredElsewhere,
	substituteKey,
	substituteOf,
	withoutStaleRecoveredElsewhere,
	withSubstitute,
} from "./substitutes.utils"
import { cancelledPartecipants, isCancelledLesson } from "./cancellation.utils"
import { isLessonContentEmpty, normalizeLessonContent } from "./lessonContent.utils"
import { normalizeSchema } from "./exerciseSchema.utils"
import {
	NOTEBOOK_EXERCISE,
	NOTEBOOK_LESSON,
	NOTEBOOK_NOTE_MAX,
	isMine,
	laterUse,
	newVariantID,
	normalizeNotebookEntry,
	notebookLessonContent,
} from "./notebook.utils"
import { isTechnicalSheetEmpty, normalizeTechnicalSheet } from "./technicalSheet.utils"

// ── CHI HA TOCCATO LA LEZIONE ─────────────────────────────────────
// Ogni scrittura su una lezione dice chi l'ha fatta, accanto a quando:
// `updatedBy`, il maestro di questa sessione. Le regole rifiutano un
// `updatedBy` che non sia chi scrive (`signsLesson` in firestore.rules): il
// nome che poi si legge sulla lezione è vero. Non firmano il nome di un
// allievo riscritto nelle sue lezioni (`syncPlayerNameInLessons`) né l'ID
// dell'evento su Google Calendar: non sono modifiche della lezione, e chi
// corregge un nome non deve risultare autore di tutte le lezioni di
// quell'allievo.
let lessonAuthorID = ""

export const setLessonAuthor = coachID => {
	lessonAuthorID = coachID ?? ""
}

const lessonStamp = (now = Timestamp.now()) => ({
	updatedAt: now,
	...(lessonAuthorID ? { updatedBy: lessonAuthorID } : {}),
})

// Chi ha collegato Google Calendar, chiesto alla copia sul telefono: senza
// rete il server non lo può dire. Un maestro che il telefono non conosce
// si dà per collegato — meglio un avviso di troppo che uno in meno.
const hasCalendarConnected = async coachID => {
	if (!coachID) return false
	try {
		const snap = await getDocFromCache(doc(firestoreDB, "coaches", coachID))
		return snap.data()?.googleCalendar?.connected === true
	} catch {
		return true
	}
}

// Le operazioni sul calendario girano in background: se falliscono la lezione
// è comunque salvata, ma l'utente deve saperlo invece di scoprirlo dal
// calendario vuoto giorni dopo.
const notifyCalendarError = async (err, message, coachID) => {
	console.error(`${message}:`, err)

	// il coach non ha mai collegato Google: silenzio, è la normalità
	if (isNotConnectedError(err)) return

	// Senza rete Google non si raggiunge: l'operazione è in coda e si rifà
	// quando la rete torna (`queueCalendarRepair`). Lo si dice una volta, a
	// chi il calendario l'ha collegato; a chi non l'ha mai fatto niente.
	if (isNetworkError(err)) {
		if (!(await hasCalendarConnected(coachID))) return
		toast.info("Google Calendar si aggiorna appena torna la rete", {
			toastId: "calendar-waits-network",
			position: "top-center",
			autoClose: 6000,
		})
		return
	}

	// `toastId`: modificando una serie l'errore si ripete per ogni lezione,
	// ma all'utente basta vederlo una volta
	if (isReconnectError(err)) {
		toast.warning("Collegamento con Google scaduto: ricollegalo dal Profilo", {
			toastId: "calendar-needs-reconnect",
			position: "top-center",
			autoClose: 8000,
		})
		return
	}

	toast.error(message, { toastId: message, position: "top-center" })
}

// Un'operazione sul calendario che non è andata si rifà dopo: quando torna
// la rete, o al controllo di ogni giorno (services/calendarSync.service.js).
// Non quando il calendario non è collegato: lì non c'è niente da rifare.
const queueCalendarFailure = (err, entry) => {
	if (!isNotConnectedError(err)) queueCalendarRepair(entry)
}

// L'evento sul calendario di un maestro che Google non l'ha collegato non
// c'è da toccare: lo si sa dalla copia del suo documento sul telefono, senza
// chiamare la Cloud Function del token una volta per lezione. Sul progetto
// dev, di nessuno (`calendarAvailable`).
const touchesCalendar = async coachID =>
	calendarAvailable() && Boolean(coachID) && (await hasCalendarConnected(coachID))

// ── SCRITTURE E CONFERMA DEL SERVER ────────────────────────────────
// Firestore applica ogni scrittura alla copia locale sul momento e la manda
// al server appena può: la promise, però, si risolve solo quando il server
// conferma, e con la rete assente o incerta quella conferma può non arrivare
// mai. Aspettarla senza limite vuol dire lasciare l'utente fermo davanti a
// una schermata che non si sblocca, per una lezione che intanto è già
// salvata e che si sincronizzerà da sola.
const SERVER_ACK_TIMEOUT = 8000

// Quando il telefono sa già di essere senza rete, la conferma non arriverà:
// aspettare otto secondi davanti a una lezione già salvata sul telefono
// sarebbe solo attesa. Mezzo secondo basta a far emergere gli errori
// immediati — una lezione che non c'è, un dato che Firestore rifiuta.
const OFFLINE_ACK_TIMEOUT = 500

// Aspetta la conferma del server, ma non oltre `SERVER_ACK_TIMEOUT`: scaduto
// quel tempo si va avanti, e la scrittura finisce di sincronizzarsi per conto
// suo. Torna `true` se il server ha confermato in tempo, `false` se la
// sincronizzazione è ancora in corso.
//
// Un errore che arriva dopo lo scadere del tempo non ha più nessuno ad
// aspettarlo: va detto da qui, altrimenti l'utente crederebbe salvato quello
// che salvato non è.
//
// Finché il server non conferma, la scrittura conta tra le modifiche da
// inviare (`trackSync`): è quello che la pastiglia in alto fa vedere.
// `pendingMessage` si può non dare, quando a dirlo ci pensa già qualcun
// altro.
const ackOrPendingSync = (writePromise, { pendingMessage, failureMessage }) =>
	new Promise((resolve, reject) => {
		let settled = false
		trackSync(writePromise)

		const timer = setTimeout(
			() => {
				settled = true
				if (pendingMessage) {
					toast.info(pendingMessage, {
						toastId: "write-pending-sync",
						position: "top-center",
						autoClose: 6000,
					})
				}
				resolve(false)
			},
			isOffline() ? OFFLINE_ACK_TIMEOUT : SERVER_ACK_TIMEOUT,
		)

		writePromise.then(
			() => {
				clearTimeout(timer)
				if (settled) return
				settled = true
				resolve(true)
			},
			error => {
				clearTimeout(timer)
				if (settled) {
					console.error(`${failureMessage}:`, error)
					toast.error(failureMessage, {
						toastId: failureMessage,
						position: "top-center",
					})
					return
				}
				settled = true
				reject(error)
			},
		)
	})

// ── SEGNALAZIONI ───────────────────────────────────────────────────
// Una segnalazione si scrive anche senza rete: a bordo campo capita, e
// chiedere di ricordarsela per dopo vuol dire perderla. Resta in coda sul
// telefono e parte quando la rete torna. Torna `true` se il server l'ha già
// ricevuta, `false` se è ancora in coda.
// `feedbackID` quando serve saperlo prima: una segnalazione che ne
// richiama un'altra (la riga sotto un errore, src/utils/crash.utils.js)
export const newFeedbackID = () => doc(collection(firestoreDB, "feedback")).id

export const addFeedback = (feedback, feedbackID = newFeedbackID()) =>
	ackOrPendingSync(
		setDoc(doc(firestoreDB, "feedback", feedbackID), {
			...feedback,
			createdAt: serverTimestamp(),
		}),
		{ failureMessage: "La segnalazione non è partita" },
	)

// Il luogo dell'evento serve a ogni lezione di una serie ricorrente: tenerlo
// in cache risparmia una lettura per lezione. Da quando il circolo si
// modifica dall'app la cache va però buttata quando l'indirizzo cambia,
// altrimenti le lezioni salvate dopo finirebbero al vecchio indirizzo.
const schoolLocations = new Map()

export const forgetSchoolLocation = schoolID => schoolLocations.delete(schoolID)

const resolveEventLocation = async schoolID => {
	if (!schoolID) return ""
	if (!schoolLocations.has(schoolID)) {
		schoolLocations.set(schoolID, buildEventLocation(await getSchool(schoolID)))
	}
	return schoolLocations.get(schoolID)
}

// L'eventID viene scritto sul documento della lezione solo quando Google
// risponde, un istante dopo il salvataggio: chi legge prima trova il campo
// vuoto. Rileggiamo il documento e, se è ancora vuoto, resta l'evento con
// l'ID ricavato dalla lezione (`calendarEventID`): è quello che la creazione
// in volo sta facendo.
const resolveEventID = async (lessonRef, lessonData) => {
	if (lessonData.eventID) return lessonData.eventID
	const snap = await getDoc(lessonRef)
	return snap.data()?.eventID || calendarEventID(lessonRef.id)
}

// Il titolo dell'evento si fa dagli allievi salvati, non da quelli scritti
// nel modulo: è quello che rifà il controllo del calendario, e i due devono
// coincidere. Racchette in classe non ha allievi del circolo.
const lessonEventTitle = lessonData =>
	buildEventTitle(
		lessonData.lessonTypeName ?? lessonData.lessonType ?? "",
		lessonData.lessonType === "racchetteInClasse" ? [] : lessonData.partecipants,
	)

// Recupera coach e normalizza le scuole in ID
export const getCoach = async coachID => {
	const coachRef = doc(firestoreDB, "coaches", coachID)
	const docSnap = await getDoc(coachRef)

	if (!docSnap.exists()) return null

	const coachData = docSnap.data()

	// helper per estrarre ID da DocumentReference
	const normalizeSchoolIDs = refs =>
		Array.isArray(refs) ? refs.map(r => r.path.split("/")[1]) : []

	const teachesInIDs = normalizeSchoolIDs(coachData.teachesIn)
	const managedSchoolsIDs = normalizeSchoolIDs(coachData.managedSchools)

	return {
		coachID: docSnap.id,
		...coachData,
		teachesIn: teachesInIDs,
		managedSchools: managedSchoolsIDs,
		notTeachingIn: normalizeSchoolIDs(coachData.notTeachingIn),
	}
}

// Listener coach e normalizzazione scuole
// Quante lezioni sono state segnate in questo circolo, in tutto. Serve solo
// a sapere se il maestro ha già cominciato: si conta senza scaricare niente,
// e il filtro sul circolo è quello che le regole pretendono comunque.
export const countSchoolLessons = async schoolID => {
	if (!schoolID) return 0

	const snapshot = await getCountFromServer(
		query(collection(firestoreDB, "lessons"), where("schoolID", "==", schoolID)),
	)
	return snapshot.data().count
}

// Chi insegna in questo circolo. Le regole lasciano leggere il documento di
// un maestro a chi condivide un circolo con lui: la query filtra sullo stesso
// circolo, quindi ogni documento che torna è uno che si può leggere.
export const subscribeToSchoolCoaches = (schoolID, callback) => {
	if (!schoolID) return () => {}

	return onSnapshot(
		query(collection(firestoreDB, "coaches"), where("schoolIDs", "array-contains", schoolID)),
		snapshot =>
			callback(
				snapshot.docs
					.map(docSnap => ({ coachID: docSnap.id, ...docSnap.data() }))
					.sort((a, b) => (a.coachName ?? "").localeCompare(b.coachName ?? "")),
			),
		err => {
			console.error("Errore sull'ascolto dei maestri del circolo:", err)
			callback([])
		},
	)
}

// Tariffe, prezzi ed email del maestro stanno in un documento che legge solo
// lui (`coaches/{id}/private/settings`): su quello condiviso li vedrebbero
// tutti i colleghi. All'app arriva un maestro solo, con dentro tutti e due.
//
// Valgono solo quelli della parte privata: sul documento condiviso, finché
// lo script di migrazione non l'ha ripulito, ci sono le copie di prima del
// passaggio, e non vanno più ascoltate.
export const COACH_PRIVATE_FIELDS = ["billingData", "schoolSettings", "schoolPlacement", "email"]

export const mergeCoachPrivate = (coachData, privateData) => {
	const merged = { ...coachData }
	COACH_PRIVATE_FIELDS.forEach(field => {
		delete merged[field]
		if (privateData?.[field] !== undefined) merged[field] = privateData[field]
	})
	return merged
}

export const subscribeToCoach = (coachID, callback) => {
	const coachRef = doc(firestoreDB, "coaches", coachID)
	const privateRef = doc(firestoreDB, "coaches", coachID, "private", "settings")

	// `undefined` finché il primo snapshot non è arrivato: si risponde solo
	// quando ci sono tutti e due, altrimenti l'app farebbe un giro di conti
	// senza tariffe
	let coachData
	let privateData

	const emit = () => {
		if (coachData === undefined || privateData === undefined) return
		if (coachData === null) {
			callback(null)
			return
		}
		callback(mergeCoachPrivate(coachData, privateData))
	}

	const onError = err => {
		// Un rifiuto delle regole vuol dire che questo accesso non ha (più)
		// diritto a questo maestro: va trattato come un maestro che non c'è,
		// altrimenti l'app resta ferma sul caricamento per sempre. Gli altri
		// errori sono di rete, e Firestore riprova da solo.
		console.error("Errore sull'ascolto del maestro:", err)
		if (err?.code === "permission-denied") callback(null)
	}

	const unsubscribeCoach = onSnapshot(
		coachRef,
		docSnap => {
			if (!docSnap.exists()) {
				// Dalla copia sul telefono "non c'è" vuol dire "non lo so":
				// senza rete un maestro mai letto qui non risulta, ma non per
				// questo va buttato fuori. Lo dice il server, quando risponde.
				if (docSnap.metadata.fromCache) return
				coachData = null
				emit()
				return
			}

			const data = docSnap.data()

			coachData = {
				coachID: docSnap.id,
				...data,
				teachesIn: normalizeSchoolIDs(data.teachesIn),
				managedSchools: normalizeSchoolIDs(data.managedSchools),
				notTeachingIn: normalizeSchoolIDs(data.notTeachingIn),
			}
			emit()
		},
		onError,
	)

	// La parte privata che non c'è, o che non si riesce a leggere, non ferma
	// la sessione: il maestro entra lo stesso, e prezzi e metodi di
	// pagamento si ereditano dal circolo.
	const unsubscribePrivate = onSnapshot(
		privateRef,
		docSnap => {
			privateData = docSnap.exists() ? docSnap.data() : {}
			emit()
		},
		err => {
			console.error("Errore sull'ascolto dei dati privati del maestro:", err)
			privateData = {}
			emit()
		},
	)

	return () => {
		unsubscribeCoach()
		unsubscribePrivate()
	}
}

// Il documento del maestro lo scrive solo il server: firebaseUID, email e
// circoli sono privilegi, e dal client passano dalle callable
// `linkCoachAccount` / `ensureCoachClaims` in functions/index.js.

// Recupera scuola a partire dallo schoolID
export const getSchool = async schoolID => {
	if (!schoolID) return null
	try {
		const schoolRef = doc(firestoreDB, "schools", schoolID)
		const docSnap = await getDoc(schoolRef)
		if (!docSnap.exists()) return null
		return { schoolID: docSnap.id, ...docSnap.data() } // non toccare i coach qui
	} catch (err) {
		console.error("Errore getSchool:", err)
		return null
	}
}

// Il documento del circolo, ascoltato: chi ne è il proprietario e la
// proposta di passarlo cambiano mentre l'app è aperta, e chi la riceve deve
// vederla senza ricaricare.
export const subscribeToSchool = (schoolID, callback) => {
	if (!schoolID) return () => {}

	return onSnapshot(
		doc(firestoreDB, "schools", schoolID),
		docSnap => docSnap.exists() && callback({ schoolID: docSnap.id, ...docSnap.data() }),
		err => console.error("Errore sull'ascolto del circolo:", err),
	)
}

// Lezioni filtrate per coach (REALTIME, solo giorno)
export const getLessonsByCoaches = (coachIDs, schoolID, selectedDate, setLessons) => {
	const lessonsRef = collection(firestoreDB, "lessons")

	// calcolo inizio/fine giornata
	const startOfDay = new Date(selectedDate)
	startOfDay.setHours(0, 0, 0, 0)

	const endOfDay = new Date(selectedDate)
	endOfDay.setHours(23, 59, 59, 999)

	// Query 1: Lezioni dove i coach sono principali
	const q1 = query(
		lessonsRef,
		where("coachID", "in", coachIDs),
		where("schoolID", "==", schoolID),
		where("lessonStart", ">=", startOfDay),
		where("lessonStart", "<=", endOfDay),
		orderBy("lessonStart"),
	)

	// Query 2: Lezioni dove i coach sono assistenti
	// Nota: se coachIDs ha più di un elemento, devo fare una query per ogni coach
	// perché array-contains non supporta "in"
	const unsubscribers = []
	const lessonsMap = new Map() // Uso Map per evitare duplicati

	// Listener per lezioni dove sono coach principale
	const unsub1 = onSnapshot(q1, snapshot => {
		snapshot.docChanges().forEach(change => {
			if (change.type === "removed") {
				lessonsMap.delete(change.doc.id)
			} else {
				lessonsMap.set(change.doc.id, { lessonID: change.doc.id, ...change.doc.data() })
			}
		})
		updateLessons()
	})
	unsubscribers.push(unsub1)

	// Listener per lezioni dove sono assistente
	coachIDs.forEach(coachID => {
		const q2 = query(
			lessonsRef,
			where("assistantCoachIDs", "array-contains", coachID),
			where("schoolID", "==", schoolID),
			where("lessonStart", ">=", startOfDay),
			where("lessonStart", "<=", endOfDay),
			orderBy("lessonStart"),
		)

		const unsub2 = onSnapshot(q2, snapshot => {
			snapshot.docChanges().forEach(change => {
				if (change.type === "removed") {
					lessonsMap.delete(change.doc.id)
				} else {
					lessonsMap.set(change.doc.id, { lessonID: change.doc.id, ...change.doc.data() })
				}
			})
			updateLessons()
		})
		unsubscribers.push(unsub2)
	})

	// Funzione per aggiornare le lezioni eliminando duplicati e ordinando
	const updateLessons = () => {
		const lessons = Array.from(lessonsMap.values()).sort(
			(a, b) => a.lessonStart?.toDate() - b.lessonStart?.toDate(),
		)
		setLessons({ [selectedDate]: lessons })
	}

	// Ritorna funzione per unsubscribe da tutti i listener
	return () => {
		unsubscribers.forEach(unsub => unsub())
	}
}

// Lezioni di un maestro su un intervallo di giorni (REALTIME).
//
// Due ascolti in tutto — da titolare e da assistente — invece di due al
// giorno. Il riepilogo del profilo ne chiede un mese alla volta: con un
// ascolto per giorno sono sessanta query sullo stesso indice, e sessanta
// risultati da riscaricare ogni volta che si cambia periodo o maestro.
//
// Torna le lezioni raggruppate per data, nella forma che vuole la cache.
export const getLessonsInRange = (coachID, schoolID, startDate, endDate, setLessonsByDate) => {
	const lessonsRef = collection(firestoreDB, "lessons")

	const rangeStart = new Date(startDate)
	rangeStart.setHours(0, 0, 0, 0)

	const rangeEnd = new Date(endDate)
	rangeEnd.setHours(23, 59, 59, 999)

	// una lezione arriva da entrambi gli ascolti se il maestro è titolare di
	// una e assistente di un'altra: la mappa la tiene una volta sola
	const lessonsMap = new Map()

	const emitByDate = () => {
		const lessonsByDate = {}
		lessonsMap.forEach(lesson => {
			const date = DateTime.fromJSDate(lesson.lessonStart?.toDate()).toISODate()
			if (!date) return
			if (!lessonsByDate[date]) lessonsByDate[date] = []
			lessonsByDate[date].push(lesson)
		})
		Object.values(lessonsByDate).forEach(dayLessons =>
			dayLessons.sort((a, b) => a.lessonStart.toDate() - b.lessonStart.toDate()),
		)
		setLessonsByDate(lessonsByDate)
	}

	const collect = snapshot => {
		snapshot.docChanges().forEach(change => {
			if (change.type === "removed") lessonsMap.delete(change.doc.id)
			else lessonsMap.set(change.doc.id, { lessonID: change.doc.id, ...change.doc.data() })
		})
		emitByDate()
	}

	const inRange = [
		where("schoolID", "==", schoolID),
		where("lessonStart", ">=", rangeStart),
		where("lessonStart", "<=", rangeEnd),
		orderBy("lessonStart"),
	]

	const unsubscribers = [
		onSnapshot(query(lessonsRef, where("coachID", "==", coachID), ...inRange), collect),
		onSnapshot(
			query(lessonsRef, where("assistantCoachIDs", "array-contains", coachID), ...inRange),
			collect,
		),
	]

	return () => unsubscribers.forEach(unsub => unsub())
}

// Le lezioni di un campo in un giorno, di tutti i maestri del circolo: il
// modulo le guarda per dire se il campo a quell'ora è già occupato. Una
// lettura sola e non un ascolto, e solo quel campo: il giorno intero del
// circolo sarebbe dieci volte tanto. Il numero del campo è una stringa, ma
// qualche lezione di prima l'ha salvato come numero.
export const getCourtLessonsOnDate = async (schoolID, court, date) => {
	const dayStart = new Date(date)
	dayStart.setHours(0, 0, 0, 0)
	const dayEnd = new Date(date)
	dayEnd.setHours(23, 59, 59, 999)

	const courtNumbers = [...new Set([String(court.courtNumber), Number(court.courtNumber)])]
	const snapshot = await getDocs(
		query(
			collection(firestoreDB, "lessons"),
			where("schoolID", "==", schoolID),
			where("court.courtsSport", "==", court.courtsSport),
			where("court.courtNumber", "in", courtNumbers),
			where("lessonStart", ">=", dayStart),
			where("lessonStart", "<=", dayEnd),
			orderBy("lessonStart"),
		),
	)
	return snapshot.docs.map(doc => ({ lessonID: doc.id, ...doc.data() }))
}

// Lezioni filtrate per scuola intera (REALTIME, solo giorno)
export const getLessonsBySchool = (schoolID, selectedDate, setLessons) => {
	const lessonsRef = collection(firestoreDB, "lessons")

	const startOfDay = new Date(selectedDate)
	startOfDay.setHours(0, 0, 0, 0)

	const endOfDay = new Date(selectedDate)
	endOfDay.setHours(23, 59, 59, 999)

	const q = query(
		lessonsRef,
		where("schoolID", "==", schoolID),
		where("lessonStart", ">=", startOfDay),
		where("lessonStart", "<=", endOfDay),
		orderBy("lessonStart"),
	)

	const unsubscribe = onSnapshot(q, snapshot => {
		const lessons = snapshot.docs.map(doc => ({
			lessonID: doc.id,
			...doc.data(),
		}))

		setLessons({ [selectedDate]: lessons })
	})

	return unsubscribe
}

export const getLessonsByDateRange = async (schoolID, coachID, startDate, endDate) => {
	const lessonsRef = collection(firestoreDB, "lessons")

	// Se non c'è un coachID specifico, ritorna tutte le lezioni della scuola
	if (!coachID) {
		const q = query(
			lessonsRef,
			where("schoolID", "==", schoolID),
			where("lessonStart", ">=", startDate),
			where("lessonStart", "<=", endDate),
			orderBy("lessonStart"),
		)
		const snapshot = await getDocs(q)
		return snapshot.docs.map(doc => ({
			lessonID: doc.id,
			...doc.data(),
		}))
	}

	// Altrimenti cerco lezioni dove il coach è principale O assistente
	const lessonsMap = new Map() // Per evitare duplicati

	// Query 1: Coach principale
	const q1 = query(
		lessonsRef,
		where("coachID", "==", coachID),
		where("schoolID", "==", schoolID),
		where("lessonStart", ">=", startDate),
		where("lessonStart", "<=", endDate),
		orderBy("lessonStart"),
	)

	// Query 2: Coach assistente
	const q2 = query(
		lessonsRef,
		where("assistantCoachIDs", "array-contains", coachID),
		where("schoolID", "==", schoolID),
		where("lessonStart", ">=", startDate),
		where("lessonStart", "<=", endDate),
		orderBy("lessonStart"),
	)

	// Eseguo entrambe le query in parallelo
	const [snapshot1, snapshot2] = await Promise.all([getDocs(q1), getDocs(q2)])

	// Unisco i risultati eliminando duplicati
	snapshot1.docs.forEach(doc => {
		lessonsMap.set(doc.id, {
			lessonID: doc.id,
			...doc.data(),
		})
	})

	snapshot2.docs.forEach(doc => {
		lessonsMap.set(doc.id, {
			lessonID: doc.id,
			...doc.data(),
		})
	})

	// Ritorno array ordinato
	return Array.from(lessonsMap.values()).sort(
		(a, b) => a.lessonStart?.toDate() - b.lessonStart?.toDate(),
	)
}

// ── LEZIONI E CALENDARIO ───────────────────────────────────────────
// Quello che legge e scrive il controllo del calendario
// (services/calendarSync.service.js).

// Le lezioni di un maestro in un circolo, in un periodo: quelle che stanno
// sul suo calendario. Dal server e non dalla copia sul telefono, che
// potrebbe non averle tutte: il controllo si fa solo con la rete.
export const getCoachLessonsInRange = async (schoolID, coachID, start, end) => {
	const snapshot = await getDocsFromServer(
		query(
			collection(firestoreDB, "lessons"),
			where("schoolID", "==", schoolID),
			where("coachID", "==", coachID),
			where("lessonStart", ">=", Timestamp.fromDate(start)),
			where("lessonStart", "<", Timestamp.fromDate(end)),
			orderBy("lessonStart"),
		),
	)
	return snapshot.docs.map(lessonDoc => ({ lessonID: lessonDoc.id, ...lessonDoc.data() }))
}

// Una lezione com'è adesso, o `null` se non c'è più. Una lezione eliminata
// le regole non la fanno leggere — guardano il circolo scritto dentro, e
// non c'è niente dentro — quindi per lei la risposta è un rifiuto.
export const getLessonByID = async lessonID => {
	try {
		const lessonSnap = await getDoc(doc(firestoreDB, "lessons", lessonID))
		return lessonSnap.exists() ? { lessonID, ...lessonSnap.data() } : null
	} catch (err) {
		if (err?.code === "permission-denied") return null
		throw err
	}
}

// il controllo del calendario la rilegge così
export const getLessonForCalendar = getLessonByID

export const saveLessonEventID = (lessonID, eventID) =>
	updateDoc(doc(firestoreDB, "lessons", lessonID), { eventID })

// ── GLI ALLIEVI, LETTI UNA VOLTA ──────────────────────────────────────
// Gli allievi di un circolo cambiano di rado, ma ascoltarli tutti voleva
// dire rileggerli tutti a ogni apertura dell'app: Firestore fa pagare da
// capo un ascolto rimasto spento più di mezz'ora. Ora si legge l'elenco
// intero al più una volta al giorno; le altre volte si parte dalla copia
// sul telefono e si ascoltano solo gli allievi cambiati da allora, chiunque
// li abbia cambiati: ogni scrittura su un allievo segna `changedAt` con
// l'ora del server (vedi playerChanged).
//
// La copia sul telefono può perdere dei pezzi (Firestore la sfoltisce
// quando si riempie, il browser la può svuotare): per accorgersene ci si
// ricorda chi c'era, e se qualcuno manca si rilegge tutto. L'elenco intero
// del giorno dopo rimette a posto anche quello che sfuggisse — un allievo
// scritto da una versione vecchia dell'app, che `changedAt` non lo segna.
const PLAYERS_FULL_READ_EVERY = 24 * 60 * 60 * 1000
const playersSyncKey = schoolID => `incampo.playersSync.${schoolID}`

// Da mettere in ogni scrittura su un allievo
export const playerChanged = () => ({ changedAt: serverTimestamp() })

// { ids, since, fullAt }: chi c'era, fino a quale modifica (ora del server,
// in millisecondi) e quando si è letto l'elenco intero l'ultima volta
const readPlayersSync = schoolID => {
	try {
		const saved = JSON.parse(window.localStorage.getItem(playersSyncKey(schoolID)))
		return Array.isArray(saved?.ids) && Number.isFinite(saved?.since) ? saved : null
	} catch {
		return null
	}
}

const savePlayersSync = (schoolID, sync) => {
	try {
		window.localStorage.setItem(playersSyncKey(schoolID), JSON.stringify(sync))
	} catch {
		// senza memoria, la prossima volta si rilegge tutto
	}
}

// come l'ordinava la query, che confronta i caratteri uno per uno
const bySurname = (a, b) => {
	const [x, y] = [a.playerSurname ?? "", b.playerSurname ?? ""]
	return x < y ? -1 : x > y ? 1 : 0
}

export const getPlayers = (schoolID, setPlayers) => {
	if (!schoolID) {
		setPlayers([])
		return () => {}
	}

	const playersRef = collection(firestoreDB, "players")
	const inSchool = where("schoolID", "==", schoolID)
	const players = new Map()
	let sync = readPlayersSync(schoolID)
	let unsubscribe = () => {}
	let isStopped = false

	const emit = () => setPlayers([...players.values()].sort(bySurname))
	const keep = docSnap => players.set(docSnap.id, { playerID: docSnap.id, ...docSnap.data() })

	// Fin dove il server ci ha portati. Le scritture ancora in viaggio non
	// contano: la loro ora non si sa.
	const remember = (snapshot, { since = sync?.since ?? 0, ...rest } = {}) => {
		if (snapshot.metadata.fromCache) return
		snapshot.docs.forEach(docSnap => {
			if (docSnap.metadata.hasPendingWrites) return
			const changedAt = docSnap.get("changedAt")?.toMillis?.() ?? 0
			if (changedAt > since) since = changedAt
		})
		sync = { ids: [], ...sync, ...rest, since }
		savePlayersSync(schoolID, sync)
	}
	// e chi c'è
	const rememberWho = () => {
		sync = { since: 0, ...sync, ids: [...players.keys()] }
		savePlayersSync(schoolID, sync)
	}

	const listenToAll = () => {
		unsubscribe = onSnapshot(
			query(playersRef, inSchool),
			snapshot => {
				players.clear()
				snapshot.docs.forEach(keep)
				emit()
				if (snapshot.metadata.fromCache) return
				rememberWho()
				remember(snapshot, { since: 0, fullAt: Date.now() })
			},
			error => console.error("Errore sull'ascolto degli allievi:", error),
		)
	}

	// Si mostra la copia sul telefono, che non costa letture e ha subito
	// quello che si scrive qui, anche senza rete (l'ora del server di una
	// scrittura in viaggio non si sa, e la query dei cambiati non la
	// vedrebbe). L'ascolto dei cambiati porta nella copia quello che si
	// scrive altrove.
	const listenToChanges = since => {
		const fromPhone = onSnapshot(
			query(playersRef, inSchool),
			{ source: "cache" },
			snapshot => {
				players.clear()
				snapshot.docs.forEach(keep)
				emit()
				rememberWho()
			},
			error => console.error("Errore sulla copia degli allievi:", error),
		)
		const fromServer = onSnapshot(
			query(playersRef, inSchool, where("changedAt", ">", Timestamp.fromMillis(since))),
			snapshot => remember(snapshot),
			error => {
				// l'indice appena pubblicato non è ancora pronto, per esempio:
				// si torna ad ascoltare tutti, come prima
				console.error("Errore sull'ascolto degli allievi cambiati:", error)
				fromPhone()
				if (!isStopped) listenToAll()
			},
		)
		unsubscribe = () => {
			fromPhone()
			fromServer()
		}
	}

	const start = async () => {
		if (sync && Date.now() - (sync.fullAt ?? 0) < PLAYERS_FULL_READ_EVERY) {
			try {
				const cached = await getDocsFromCache(query(playersRef, inSchool))
				const found = new Set(cached.docs.map(docSnap => docSnap.id))
				if (isStopped) return
				if (sync.ids.every(id => found.has(id))) {
					listenToChanges(sync.since)
					return
				}
			} catch {
				// la copia non si legge: si rilegge tutto
			}
		}
		if (!isStopped) listenToAll()
	}
	start()

	return () => {
		isStopped = true
		unsubscribe()
	}
}

// I cataloghi (sport, superfici, tipi di lezione, metodi di pagamento) sono
// uguali per tutti e cambiano quasi mai: chiederli al server a ogni avvio
// voleva dire rileggerli tutti ogni volta. Si leggono dalla copia sul
// telefono, e dal server al più una volta al giorno — o quando la copia non
// c'è ancora.
const CATALOG_MAX_AGE = 24 * 60 * 60 * 1000
const readCatalog = async name => {
	const catalog = collection(firestoreDB, name)
	const fetchedAtKey = `incampo.catalog.${name}`
	let fetchedAt = 0
	try {
		fetchedAt = Number(window.localStorage.getItem(fetchedAtKey)) || 0
	} catch {
		// senza memoria si chiede al server, come prima
	}
	if (Date.now() - fetchedAt < CATALOG_MAX_AGE) {
		try {
			const cached = await getDocsFromCache(catalog)
			if (!cached.empty) return cached
		} catch {
			// la copia non c'è: si chiede al server
		}
	}
	const snapshot = await getDocs(catalog)
	if (!snapshot.metadata.fromCache && !snapshot.empty)
		try {
			window.localStorage.setItem(fetchedAtKey, String(Date.now()))
		} catch {
			// la prossima volta si richiede, pazienza
		}
	return snapshot
}

export const getPaymentMethods = async () => {
	const snapshot = await readCatalog("payment_methods")
	const data = {}
	snapshot.forEach(doc => {
		data[doc.id] = doc.data()
	})
	return data
}

export const getLessonTypes = async () => {
	const snapshot = await readCatalog("lesson_types")
	const data = {}
	snapshot.forEach(doc => {
		// come si comporta un tipo di lezione lo dice il documento, ma un
		// documento che non lo dice ancora deve comportarsi come prima:
		// vedi withLessonTypeDefaults
		data[doc.id] = withLessonTypeDefaults(doc.id, doc.data())
	})
	return data
}

export const getSurfaces = async () => {
	const snapshot = await readCatalog("surfaces")
	const data = {}
	snapshot.forEach(doc => {
		// il tipo colora il campo nell'interfaccia, il nome è quello che si
		// legge: i documenti più vecchi hanno solo l'id, quindi vale da entrambi
		const surface = doc.data()
		data[doc.id] = {
			surfaceType: surface.surfaceType ?? doc.id,
			surfaceName: surface.surfaceName ?? doc.id,
			...surface,
		}
	})
	return data
}

export const getSports = async () => {
	const snapshot = await readCatalog("sports")
	const data = {}
	snapshot.forEach(doc => {
		data[doc.id] = doc.data()
	})
	return data
}

// Array piatto degli ID dei partecipanti: è l'unica forma che Firestore sa
// indicizzare, e permette di cercare le lezioni di un allievo con una query
// sola invece di scorrere lo storico del circolo
const getPartecipantIDs = partecipants =>
	(partecipants || []).map(partecipant => partecipant?.playerID).filter(Boolean)

// Campi con cui nasce un allievo: li teniamo tutti valorizzati così le schede
// e i form non devono ragionare su campi assenti
export const EMPTY_PLAYER = {
	playerName: "",
	playerSurname: "",
	// Come lo chiami davvero: distingue due omonimi senza bisogno di un
	// documento, ed è il modo in cui l'allievo esiste davvero in campo.
	nickname: "",
	CF: "",
	birthday: "",
	birthplace: "",
	mail: "",
	medCertificateExpiration: "",
	memberType: "",
	parentBirthday: "",
	parentBirthplace: "",
	parentCF: "",
	phoneNumber: "",
	playerParentName: "",
	playerParentSurname: "",
	residence: "",
	size: "",
}

// Cerca tra gli allievi della scuola quello che il maestro sta scrivendo:
// nome completo, etichetta dell'elenco o soprannome (vedi `findKnownPlayer`).
// `knownPlayers` è la lista già in memoria (provider allievi): se c'è la uso,
// così il confronto ignora maiuscole e spazi. Altrimenti interrogo Firestore.
const findPlayerByTypedName = async (typedName, schoolID, knownPlayers) => {
	const normalizedName = normalizeName(typedName)
	if (!normalizedName || !schoolID) return null

	if (knownPlayers?.length > 0) {
		const found = findKnownPlayer(typedName, knownPlayers)
		if (found) return found.playerID
	}

	// fallback: confronto sul nome proprio, poi verifico il nome completo.
	// Il filtro sul circolo non è un di più: senza, le regole rifiutano la
	// query, perché non saprebbero dire in anticipo se il risultato è tutto
	// leggibile da chi la sta facendo.
	const nameTokens = typedName.trim().split(/\s+/)
	const isReadable = docSnap => !docSnap.data().archived

	const byName = await getDocs(
		query(
			collection(firestoreDB, "players"),
			where("schoolID", "==", schoolID),
			where("playerName", "==", nameTokens[0]),
		),
	)
	const nameMatch = byName.docs.find(
		docSnap =>
			isReadable(docSnap) &&
			normalizeName(getPlayerFullName(docSnap.data())) === normalizedName,
	)
	if (nameMatch) return nameMatch.id

	const byNickname = await getDocs(
		query(
			collection(firestoreDB, "players"),
			where("schoolID", "==", schoolID),
			where("nickname", "==", typedName.trim()),
		),
	)

	return byNickname.docs.find(isReadable)?.id ?? null
}

export const getOrCreatePlayer = async (player, schoolID, knownPlayers) => {
	// ✅ Se il giocatore ha già un ID → ritorno subito
	if (player.playerID) return player.playerID

	// Un allievo senza circolo non sarebbe leggibile da nessuno
	if (!schoolID) throw new Error("getOrCreatePlayer: schoolID mancante")

	// ✅ Cerco se esiste già un giocatore che si scrive così
	const existingPlayerID = await findPlayerByTypedName(player.playerName, schoolID, knownPlayers)
	if (existingPlayerID) return existingPlayerID

	// ✅ Una parola sola diventa soprannome, due o più nome e cognome
	const { playerName, playerSurname, nickname } = splitTypedPlayerName(player.playerName)

	// ✅ Creo un nuovo giocatore “volante”
	const newPlayerRef = doc(collection(firestoreDB, "players"))

	// L'ID si conosce prima di scrivere: la lezione non deve aspettare il
	// server per citarlo, altrimenti senza rete un nome nuovo terrebbe
	// fermo il salvataggio della lezione. Che si sincronizzerà, lo dice già
	// la lezione.
	await ackOrPendingSync(
		setDoc(newPlayerRef, {
			...EMPTY_PLAYER,
			playerID: newPlayerRef.id,
			playerName,
			playerSurname,
			nickname,
			archived: false,
			createdAt: Timestamp.now(),
			isTemporary: true,
			schoolID,
			...playerChanged(),
		}),
		{ failureMessage: `L'allievo ${player.playerName} non è stato salvato` },
	)

	return newPlayerRef.id
}

// Gli allievi di una lezione si risolvono tutti insieme, una volta sola.
// Un nome scritto a mano non esiste ancora mentre lo si scrive: due ricerche
// avviate nello stesso momento non lo trovano né l'una né l'altra, e lo
// creano tutte e due. Succedeva a ogni serie ricorrente — le lezioni partono
// insieme — e l'elenco allievi si riempiva dello stesso nome ripetuto una
// volta per lezione. Qui si va in fila e ci si ricorda chi si è già risolto,
// così lo stesso nome porta sempre a un allievo solo.
//
// Torna partecipanti nuovi invece di scrivere il playerID dentro quelli che
// arrivano: erano lo stato del modulo, condiviso da tutte le lezioni della
// serie, e riscriverlo da più lezioni insieme lasciava la stessa persona con
// un ID diverso da una lezione all'altra.
export const resolvePartecipants = async (partecipants, schoolID, knownPlayers) => {
	const resolvedByTypedName = new Map()
	const resolved = []

	// Riconosciuto l'allievo, sulla lezione va il suo nome com'è scritto
	// nella scheda: scrivere "marco muratori" o "Bibo" è un modo di
	// cercarlo, non di rinominarlo.
	const withKnownName = player => {
		const known = knownPlayers?.find(candidate => candidate.playerID === player.playerID)
		const knownName = known && getPlayerFullName(known)
		return knownName ? { ...player, playerName: knownName } : player
	}

	for (const player of partecipants ?? []) {
		// un posto ancora senza nome non è un allievo da cercare, né da
		// creare: resta un posto, e si riempie quando il nome si sa
		if (isPlaceholderPartecipant(player)) {
			resolved.push({ ...player, playerName: "", playerID: "" })
			continue
		}

		if (player.playerID) {
			resolved.push(withKnownName(player))
			continue
		}

		const typedName = normalizeName(player.playerName)
		if (!resolvedByTypedName.has(typedName)) {
			resolvedByTypedName.set(
				typedName,
				await getOrCreatePlayer(player, schoolID, knownPlayers),
			)
		}

		resolved.push(withKnownName({ ...player, playerID: resolvedByTypedName.get(typedName) }))
	}

	return resolved
}

// Gli stessi partecipanti, ma seguendo la regola della lezione: racchette in
// classe è una classe intera, non un elenco di allievi del circolo.
export const resolveLessonPartecipants = async lessonData =>
	lessonData.lessonType === "racchetteInClasse"
		? []
		: resolvePartecipants(
				lessonData.partecipants,
				lessonData.schoolID,
				lessonData.knownPlayers,
			)

// Crea un allievo dalla sezione Allievi: qui i dati sono compilati a mano,
// quindi non è un allievo "volante"
export const createPlayer = async (playerData, schoolID) => {
	if (!schoolID) throw new Error("createPlayer: schoolID mancante")

	const newPlayerRef = doc(collection(firestoreDB, "players"))

	await ackOrPendingSync(
		setDoc(newPlayerRef, {
			...EMPTY_PLAYER,
			...playerData,
			playerID: newPlayerRef.id,
			archived: false,
			isTemporary: false,
			createdAt: Timestamp.now(),
			schoolID,
			...playerChanged(),
		}),
		{
			pendingMessage: "Allievo salvato: si sincronizzerà appena torna la rete",
			failureMessage: "L'allievo non è stato salvato",
		},
	)

	return newPlayerRef.id
}

// Crea in blocco gli allievi che arrivano da un elenco importato. Firestore
// accetta 500 scritture per volta: si va a blocchi, e se il secondo blocco
// fallisce il primo resta — meglio metà elenco importato che un errore e
// niente, visto che i doppioni li riconosciamo dal nome.
export const createPlayers = async (playersData, schoolID) => {
	if (!schoolID) throw new Error("createPlayers: schoolID mancante")

	const BLOCK_SIZE = 400
	let created = 0

	for (let start = 0; start < playersData.length; start += BLOCK_SIZE) {
		const batch = writeBatch(firestoreDB)

		playersData.slice(start, start + BLOCK_SIZE).forEach(playerData => {
			const playerRef = doc(collection(firestoreDB, "players"))
			batch.set(playerRef, {
				...EMPTY_PLAYER,
				...playerData,
				playerID: playerRef.id,
				archived: false,
				isTemporary: false,
				createdAt: Timestamp.now(),
				schoolID,
				...playerChanged(),
			})
		})

		await ackOrPendingSync(batch.commit(), {
			pendingMessage: "Allievi salvati: si sincronizzeranno appena torna la rete",
			failureMessage: "Una parte degli allievi non è stata salvata",
		})
		created += Math.min(BLOCK_SIZE, playersData.length - start)
	}

	return { created }
}

// Query indicizzata delle lezioni di un allievo, dalla più recente.
// Serve l'indice composito: schoolID + partecipantIDs (array) + lessonStart desc
const playerLessonsQuery = (schoolID, playerID, pageSize, cursor) => {
	const constraints = [
		where("schoolID", "==", schoolID),
		where("partecipantIDs", "array-contains", playerID),
		orderBy("lessonStart", "desc"),
		limit(pageSize),
	]
	if (cursor) constraints.push(startAfter(cursor))

	return query(collection(firestoreDB, "lessons"), ...constraints)
}

// Lezioni di un allievo. Ritorna anche il cursore per caricare le successive:
// lo storico completo può essere lungo.
export const getPlayerLessons = async (schoolID, player, { pageSize = 10, cursor = null } = {}) => {
	if (!schoolID || !player?.playerID) return { lessons: [], cursor: null, hasMore: false }

	const snapshot = await getDocs(playerLessonsQuery(schoolID, player.playerID, pageSize, cursor))

	return {
		lessons: snapshot.docs.map(docSnap => ({ lessonID: docSnap.id, ...docSnap.data() })),
		cursor: snapshot.docs[snapshot.docs.length - 1] ?? cursor,
		hasMore: snapshot.size === pageSize,
	}
}

// Riallinea il nome del giocatore dentro le lezioni già salvate: i partecipanti
// sono denormalizzati, quindi senza questo passaggio le vecchie lezioni
// continuerebbero a mostrare il nome sbagliato.
export const syncPlayerNameInLessons = async (schoolID, player, previousFullName) => {
	if (!schoolID || !player?.playerID) return { updated: 0 }

	const pageSize = 200
	const nextFullName = getPlayerFullName(player)
	let cursor = null
	let updated = 0

	// scorro solo le sue lezioni, a blocchi, e riscrivo quelle che cambiano
	for (;;) {
		const snapshot = await getDocs(
			playerLessonsQuery(schoolID, player.playerID, pageSize, cursor),
		)
		if (snapshot.empty) break

		const batch = writeBatch(firestoreDB)
		let batchSize = 0

		snapshot.docs.forEach(docSnap => {
			const partecipants = docSnap.data().partecipants || []
			let hasChanges = false

			const updatedPartecipants = partecipants.map(partecipant => {
				const isSamePlayer = partecipant?.playerID
					? partecipant.playerID === player.playerID
					: normalizeName(partecipant?.playerName) === normalizeName(previousFullName)

				if (!isSamePlayer) return partecipant
				if (
					partecipant.playerName === nextFullName &&
					partecipant.playerID === player.playerID
				)
					return partecipant

				hasChanges = true
				return { ...partecipant, playerName: nextFullName, playerID: player.playerID }
			})

			if (!hasChanges) return

			batch.update(docSnap.ref, {
				partecipants: updatedPartecipants,
				partecipantIDs: getPartecipantIDs(updatedPartecipants),
			})
			batchSize++
		})

		if (batchSize > 0) {
			await batch.commit()
			updated += batchSize
		}

		cursor = snapshot.docs[snapshot.docs.length - 1]
		if (snapshot.size < pageSize) break
	}

	return { updated }
}

// Modifica un allievo. Se cambia il nome, riallineo anche le lezioni: capita
// spesso di segnare solo il nome e sistemare il cognome più avanti.
export const editPlayer = async (playerID, playerData, { previousPlayer } = {}) => {
	if (!playerID) throw new Error("editPlayer: playerID mancante")

	const playerRef = doc(firestoreDB, "players", playerID)

	const updatedPlayer = { ...previousPlayer, ...playerData, playerID }
	const previousFullName = getPlayerFullName(previousPlayer)
	const nextFullName = getPlayerFullName(updatedPlayer)
	const schoolID = previousPlayer?.schoolID ?? playerData?.schoolID

	const playerWrite = updateDoc(playerRef, {
		...playerData,
		isTemporary: false,
		updatedAt: Timestamp.now(),
		...playerChanged(),
	})
	const acked = await ackOrPendingSync(playerWrite, {
		pendingMessage: "Allievo salvato: si sincronizzerà appena torna la rete",
		failureMessage: "L'allievo non è stato salvato",
	})

	if (previousFullName === nextFullName) return { success: true, lessonsUpdated: 0 }

	// Il nome nelle lezioni si riallinea quando il server ha l'allievo: le
	// lezioni da cercare stanno lì, e senza rete il telefono troverebbe
	// solo quelle che ha già visto — un nome cambiato a metà. Si aspetta
	// la rete, e intanto conta tra le modifiche da inviare.
	if (!acked) {
		const rename = playerWrite.then(
			() => syncPlayerNameInLessons(schoolID, updatedPlayer, previousFullName),
			// se non passa l'allievo, lo dice già chi aspettava lui
			() => null,
		)
		trackSync(rename)
		rename.catch(error => {
			console.error("Nome dell'allievo non aggiornato nelle lezioni:", error)
			toast.error("Il nome nuovo dell'allievo non è arrivato nelle sue lezioni", {
				toastId: "player-rename-failed",
				position: "top-center",
			})
		})
		return { success: true, lessonsUpdated: 0, pendingSync: true }
	}

	const { updated } = await syncPlayerNameInLessons(schoolID, updatedPlayer, previousFullName)
	return { success: true, lessonsUpdated: updated }
}

// La scheda tecnica dell'allievo (src/utils/technicalSheet.utils.js): si
// scrive solo da qui, e solo lei, così il foglio dell'anagrafica non la
// tocca e la scheda non tocca il resto. Vuota, il campo si toglie. La
// modifica chiunque insegni nel circolo, come l'allievo.
export const editPlayerTechnicalSheet = async (playerID, sheet) => {
	const normalized = normalizeTechnicalSheet(sheet)
	const write = updateDoc(doc(firestoreDB, "players", playerID), {
		technicalSheet: isTechnicalSheetEmpty(normalized) ? deleteField() : normalized,
		updatedAt: Timestamp.now(),
		...playerChanged(),
	})

	try {
		return await ackOrPendingSync(write, {
			pendingMessage: "Scheda salvata: si sincronizzerà appena torna la rete",
			failureMessage: "La scheda tecnica non è stata salvata",
		})
	} catch (error) {
		console.error("Errore editPlayerTechnicalSheet:", error)
		throw error
	}
}

// L'allievo non si elimina: si archivia, così lezioni e pagamenti già salvati
// restano coerenti e si può sempre riportarlo tra gli attivi
export const archivePlayer = async playerID => {
	const playerRef = doc(firestoreDB, "players", playerID)
	await ackOrPendingSync(
		updateDoc(playerRef, {
			archived: true,
			archivedAt: Timestamp.now(),
			updatedAt: Timestamp.now(),
			...playerChanged(),
		}),
		{
			pendingMessage: "Allievo archiviato: si sincronizzerà appena torna la rete",
			failureMessage: "L'allievo non è stato archiviato",
		},
	)
	return { success: true }
}

export const restorePlayer = async playerID => {
	const playerRef = doc(firestoreDB, "players", playerID)
	await ackOrPendingSync(
		updateDoc(playerRef, {
			archived: false,
			archivedAt: null,
			updatedAt: Timestamp.now(),
			...playerChanged(),
		}),
		{
			pendingMessage: "Allievo ripristinato: si sincronizzerà appena torna la rete",
			failureMessage: "L'allievo non è stato ripristinato",
		},
	)
	return { success: true }
}

// L'abbonamento che copre una lezione. Chiederlo a Firestore con due
// intervalli — inizio prima della lezione, fine dopo — pretende un indice
// con le date in un ordine preciso, e quello che c'era non era l'ordine
// giusto: la ricerca falliva, e con lei allievi e pagamenti della lezione
// ("Lezione salvata, ma non allievi e pagamenti") ogni volta che un
// allievo pagava con l'abbonamento. Gli abbonamenti attivi di un allievo
// sono pochi: si chiedono per uguaglianza, che non vuole indici, e le
// date si guardano qui. E se la ricerca non passa lo stesso, l'allievo si
// salva comunque, senza abbonamento collegato: meglio che perderlo.
const findCoveringSubscription = async (schoolID, playerID, lessonStart) => {
	try {
		const lessonMillis = lessonStart?.toMillis?.()
		if (lessonMillis == null) return null
		const subscriptionDocs = await getDocs(
			query(
				collection(firestoreDB, "subscriptions"),
				where("schoolID", "==", schoolID),
				where("playerRef", "==", playerID),
				where("isActive", "==", true),
			),
		)
		const covering = subscriptionDocs.docs.find(subscriptionDoc => {
			const { startDate, endDate } = subscriptionDoc.data()
			return (
				startDate?.toMillis?.() <= lessonMillis && endDate?.toMillis?.() >= lessonMillis
			)
		})
		return covering?.ref ?? null
	} catch (error) {
		console.error("Abbonamento dell'allievo non trovato:", error)
		return null
	}
}

// Senza rete una lezione si può eliminare prima che il server abbia
// confermato di averla appena modificata. Quello che parte dopo la conferma
// (allievi e pagamenti della modifica) non va scritto per una lezione che
// non c'è più: i pagamenti resterebbero orfani, e gli allievi le regole li
// rifiutano. La copia sul telefono lo sa già, senza chiedere niente al
// server: la cancellazione in coda vale anche lì.
const lessonStillExists = async lessonRef => {
	try {
		return (await getDocFromCache(lessonRef)).exists()
	} catch {
		// non è sul telefono: non lo sappiamo, e si va avanti come prima
		return true
	}
}

// I pagamenti rimasti a una lezione eliminata senza rete: quelli che il
// telefono non aveva mai visto, e che quindi la cancellazione non ha
// trovato. Si cercano sul server quando la cancellazione è arrivata. Gli
// allievi della lezione no: senza la lezione le regole non li fanno più
// leggere né scrivere a nessuno, e non li conta niente.
const deleteLeftoverPayments = async (lessonID, schoolID) => {
	const leftovers = await getDocsFromServer(
		query(
			collection(firestoreDB, "payments"),
			where("schoolID", "==", schoolID),
			where("lessonID", "==", lessonID),
		),
	)
	if (leftovers.empty) return

	const batch = writeBatch(firestoreDB)
	leftovers.forEach(payment => batch.delete(payment.ref))
	await batch.commit()
	console.info(`Tolti ${leftovers.size} pagamenti rimasti alla lezione ${lessonID}`)
}

// Aggiunge una lezione
export const addLesson = async lessonData => {
	try {
		// 1️⃣ Assicuro il playerID a ogni partecipante. Chi crea una serie
		// li ha già risolti una volta per tutte: qui li ritrova con l'ID
		// addosso, e non cerca più niente.
		// Un'assenza recuperata altrove resta segnata solo se è ancora
		// un'assenza: duplicando, chi era assente torna presente.
		const partecipants = withoutStaleRecoveredElsewhere(
			await resolveLessonPartecipants(lessonData),
		)

		// i posti senza nome non hanno un allievo da registrare
		const namedPartecipants = partecipants.filter(p => !isPlaceholderPartecipant(p))

		// 2️⃣ L'abbonamento che copre chi paga così. È una lettura — senza
		// rete risponde la copia sul telefono — e in un batch si scrive
		// soltanto: si cerca prima, per tutti insieme.
		const subscriptionRefs = await Promise.all(
			namedPartecipants.map(player =>
				player.paymentMethod === "subscription"
					? findCoveringSubscription(
							lessonData.schoolID,
							player.playerID,
							lessonData.lessonStart,
						)
					: null,
			),
		)

		// 3️⃣ Lezione, pagamenti e allievi in una scrittura sola. Prima gli
		// allievi aspettavano che il server confermasse la lezione, perché la
		// regola che li protegge la leggeva dal server: senza rete restavano
		// nella memoria dell'app, e chiusa l'app la lezione rimaneva senza
		// allievi né pagamenti — e gli incassi sbagliati. La regola adesso
		// accetta anche la lezione che nasce nello stesso batch
		// (`canWriteInLesson` in firestore.rules), e il batch va in coda tutto
		// insieme: sopravvive all'app chiusa, e arriva tutto o niente.
		//
		// Un batch tiene al massimo 500 scritture. Una lezione ne fa una, più
		// due per allievo (pagamento e partecipante): ci stanno 249 allievi,
		// e una lezione vera ne ha molti meno.
		const lessonRef = doc(collection(firestoreDB, "lessons"))
		const batch = writeBatch(firestoreDB)
		// creata e firmata nello stesso istante: è così che la lezione sa di
		// non essere mai stata modificata dopo
		const createdAt = Timestamp.now()
		batch.set(lessonRef, {
			lessonStart: lessonData.lessonStart ?? null,
			lessonEnd: lessonData.lessonEnd ?? null,
			lessonType: lessonData.lessonType ?? "",
			usingLights: lessonData.usingLights ?? false,
			usingHeat: lessonData.usingHeat ?? false,
			lessonNotes: lessonData.lessonNotes ?? "",
			satLevel: lessonData.satLevel ?? null,
			ptrLevel: lessonData.ptrLevel ?? null,
			partecipants,
			partecipantsCount: partecipants.length,
			partecipantIDs: getPartecipantIDs(partecipants),
			tracksRecovery: lessonTracksRecovery(partecipants),
			court: lessonData.court ?? "",
			// chi paga il campo e a chi è fatturata restano scritti sulla
			// lezione: cambiare domani le regole del circolo non deve
			// riscrivere i conti di oggi
			courtPaidBy: lessonData.courtPaidBy ?? COURT_PAID_BY_COACH,
			billedTo: lessonData.billedTo ?? BILLED_TO_PLAYERS,
			eventID: lessonData.eventID ?? "",
			coachID: lessonData.coachID ?? "",
			assistantCoachIDs: lessonData.assistantCoachIDs ?? "",
			sportID: lessonData.sportID ?? "",
			schoolID: lessonData.schoolID ?? "",
			createdAt,
			createdBy: lessonData.createdBy ?? "",
			...lessonStamp(createdAt),
			recurrencyID: lessonData.recurrencyID ?? "",
			archived: lessonData.archived ?? false,
		})

		namedPartecipants.forEach((player, index) => {
			const playerID = player.playerID

			// 4️⃣ Pagamento singolo: l'ID si conosce prima di scriverlo, e il
			// partecipante lo porta già con sé
			let paymentRef = null
			if (player.paymentMethod && player.paymentMethod !== "subscription") {
				paymentRef = doc(collection(firestoreDB, "payments"))
				batch.set(paymentRef, {
					paymentDate: lessonData.lessonStart,
					paymentMethod: player.paymentMethod,
					paymentAmount: player.paymentAmount ?? 0,
					playerID,
					lessonID: lessonRef.id,
					// il maestro sta sulla lezione, non qui: senza copiarcelo
					// un totale per maestro andrebbe ricavato rileggendo le
					// lezioni una per una, che è esattamente quello che si
					// vorrebbe evitare sommando i pagamenti
					coachID: lessonData.coachID ?? "",
					schoolID: lessonData.schoolID,
					createdAt: Timestamp.now(),
				})
			}

			// 5️⃣ Partecipante nella subcollection della lezione
			batch.set(doc(firestoreDB, "lessons", lessonRef.id, "participants", playerID), {
				playerID,
				lessonID: lessonRef.id,
				paymentID: paymentRef?.id ?? "",
				paymentMethod: player.paymentMethod ?? "",
				isPresent: player.isPresent ?? true,
				subscriptionID: subscriptionRefs[index]?.id ?? "",
				createdAt: Timestamp.now(),
			})
		})

		const lessonWrite = batch.commit()

		// 6️⃣ Creazione evento calendario → asincrona, NON blocca la funzione
		;(async () => {
			if (!(await touchesCalendar(lessonData.coachID))) return
			try {
				const eventLocation = await resolveEventLocation(lessonData.schoolID)
				const eventID = await createCalendarEvent({
					...lessonData,
					partecipants,
					lessonID: lessonRef.id,
					eventTitle: lessonEventTitle({ ...lessonData, partecipants }),
					eventLocation,
				})
				if (eventID) {
					await updateDoc(lessonRef, { eventID })
					console.info(
						`Evento Google Calendar ${eventID} creato — luogo: ${
							eventLocation || "nessuno"
						}`,
					)
				}
			} catch (err) {
				queueCalendarFailure(err, { type: "lesson", lessonID: lessonRef.id })
				notifyCalendarError(
					err,
					"Lezione salvata, ma non sincronizzata su Google Calendar",
					lessonData.coachID,
				)
			}
		})()

		await ackOrPendingSync(lessonWrite, {
			pendingMessage: "Lezione salvata: si sincronizzerà appena torna la rete",
			failureMessage: "La lezione non è stata salvata",
		})

		return lessonRef
	} catch (error) {
		console.error("Errore durante la creazione della lezione:", error)
		throw error
	}
}

// Modifica una lezione
export const editLesson = async (lessonData, editSeries = false) => {
	try {
		const { lessonID, recurrencyID, lessonStart, lessonEnd } = lessonData

		// Le lezioni di una serie si cercano: senza rete si troverebbero solo
		// quelle che il telefono ha già visto, e la serie cambierebbe a metà
		// senza che nessuno se ne accorga.
		if (editSeries && recurrencyID) {
			requireNetwork(
				"Per modificare tutta la serie serve la rete: senza, si può modificare solo questa lezione",
			)
		}

		const lessonRef = doc(firestoreDB, "lessons", lessonID)

		// Gli allievi si risolvono qui, una volta per tutta la serie. Farlo
		// dentro ogni lezione — che partono tutte insieme — voleva dire che
		// un nome aggiunto a mano non lo trovava nessuna, e ognuna se ne
		// creava uno suo: lo stesso allievo ripetuto una volta per lezione.
		// Chi nel modulo è tornato presente perde il segno di un'assenza
		// recuperata altrove: non ha più un'assenza.
		const partecipants =
			lessonData.lessonType === "racchetteInClasse"
				? lessonData.partecipants
				: withoutStaleRecoveredElsewhere(
						await resolvePartecipants(
							lessonData.partecipants,
							lessonData.schoolID,
							lessonData.knownPlayers,
						),
					)
		const resolvedLessonData = { ...lessonData, partecipants }

		/**
		 * Aggiorna una singola lezione su Firestore
		 */
		const updateOneLessonFirestore = async (lessonRef, dataForThisDoc) => {
			// 1️⃣ Aggiorno il documento della lezione, e allievi e pagamenti
			// solo dopo che il server l'ha confermato. Non un batch solo come in
			// `addLesson`: per rimettere a posto i pagamenti bisogna sapere
			// quali sono già salvati, e senza rete il telefono può non averli
			// visti — i pagamenti di una lezione si leggono solo quando la si
			// modifica, la si elimina o ci si mette un sostituto. Un batch
			// costruito sulla copia del telefono rifarebbe da capo quelli che
			// non trova: lo stesso incasso contato due volte. Dopo la conferma
			// le letture qui sotto vanno al server.
			await updateDoc(lessonRef, {
				lessonStart: dataForThisDoc.lessonStart ?? null,
				lessonEnd: dataForThisDoc.lessonEnd ?? null,
				lessonType: dataForThisDoc.lessonType ?? "",
				usingLights: dataForThisDoc.usingLights ?? false,
				usingHeat: dataForThisDoc.usingHeat ?? false,
				lessonNotes: dataForThisDoc.lessonNotes ?? "",
				satLevel: dataForThisDoc.satLevel ?? null,
				ptrLevel: dataForThisDoc.ptrLevel ?? null,
				partecipants:
					dataForThisDoc.lessonType !== "racchetteInClasse"
						? dataForThisDoc.partecipants
						: [],
				partecipantsCount:
					dataForThisDoc.lessonType !== "racchetteInClasse"
						? dataForThisDoc.partecipants.length
						: 0,
				partecipantIDs:
					dataForThisDoc.lessonType !== "racchetteInClasse"
						? getPartecipantIDs(dataForThisDoc.partecipants)
						: [],
				tracksRecovery:
					dataForThisDoc.lessonType !== "racchetteInClasse" &&
					lessonTracksRecovery(dataForThisDoc.partecipants),
				court: dataForThisDoc.court ?? "",
				courtPaidBy: dataForThisDoc.courtPaidBy ?? COURT_PAID_BY_COACH,
				billedTo: dataForThisDoc.billedTo ?? BILLED_TO_PLAYERS,
				coachID: dataForThisDoc.coachID ?? "",
				assistantCoachIDs: dataForThisDoc.assistantCoachIDs ?? "",
				sportID: dataForThisDoc.sportID ?? "",
				archived: dataForThisDoc.archived ?? false,
				...lessonStamp(),
			})
			if (!(await lessonStillExists(lessonRef))) return

			// 2️⃣ Gestione subcollection participants
			const participantsRef = collection(firestoreDB, "lessons", lessonRef.id, "participants")
			const snapshot = await getDocs(participantsRef)
			const currentParticipants = snapshot.docs.map(d => d.id)
			const newParticipantIds = []

			// 2️⃣.bis I pagamenti di questa lezione. Prima ogni salvataggio
			// ne creava di nuovi e lasciava lì i precedenti: la stessa
			// lezione modificata tre volte risultava incassata tre volte.
			// Si riparte da quelli già salvati e si riusano — la pulizia dei
			// doppioni di allora è compresa.
			const savedPaymentsSnapshot = await getDocs(
				query(
					collection(firestoreDB, "payments"),
					where("schoolID", "==", dataForThisDoc.schoolID),
					where("lessonID", "==", lessonRef.id),
				),
			)
			const { paymentByPlayer, obsoletePayments } = reconcileLessonPayments({
				savedPayments: savedPaymentsSnapshot.docs.map(paymentDoc => ({
					playerID: paymentDoc.data().playerID,
					ref: paymentDoc.ref,
				})),
				partecipants: dataForThisDoc.partecipants || [],
				newPaymentRef: () => doc(collection(firestoreDB, "payments")),
			})

			// i posti senza nome non hanno un allievo da registrare: il loro
			// posto nella lezione basta
			const namedPartecipants = (dataForThisDoc.partecipants || []).filter(
				player => !isPlaceholderPartecipant(player),
			)
			const sets = namedPartecipants.map(async player => {
				let subscriptionRef = null
				const playerID = player.playerID // già risolto per tutta la serie
				newParticipantIds.push(playerID)
				const payment = paymentByPlayer.get(playerID)

				// Gestione pagamenti
				if (payment) {
					await setDoc(
						payment.ref,
						{
							paymentDate: dataForThisDoc.lessonStart,
							paymentMethod: player.paymentMethod ?? "",
							paymentAmount: player.paymentAmount ?? 0,
							playerID,
							lessonID: lessonRef.id,
							coachID: dataForThisDoc.coachID ?? "",
							...(player.paidTo ? { paidTo: player.paidTo } : {}),
							schoolID: dataForThisDoc.schoolID,
							...(payment.isNew ? { createdAt: Timestamp.now() } : {}),
							updatedAt: Timestamp.now(),
						},
						{ merge: true },
					)
				} else if (player.paymentMethod === "subscription") {
					subscriptionRef = await findCoveringSubscription(
						dataForThisDoc.schoolID,
						playerID,
						dataForThisDoc.lessonStart,
					)
				}

				return setDoc(
					doc(participantsRef, playerID),
					{
						playerID,
						lessonID: lessonRef.id,
						paymentID: payment?.ref.id ?? "",
						paymentMethod: player.paymentMethod ?? "",
						isPresent: player.isPresent ?? true,
						subscriptionID: subscriptionRef?.id ?? "",
						updatedAt: Timestamp.now(),
					},
					{ merge: true },
				)
			})

			const deletes = currentParticipants
				.filter(oldId => !newParticipantIds.includes(oldId))
				.map(oldId => deleteDoc(doc(participantsRef, oldId)))

			await Promise.all([
				...deletes,
				...sets,
				...obsoletePayments.map(paymentRef => deleteDoc(paymentRef)),
			])
		}

		/**
		 * Aggiorna evento su Calendar in background. Se l'evento non c'è lo
		 * rifà, e se è nato con un ID nuovo lo scrive sulla lezione.
		 */
		const updateOneLessonCalendar = async (lessonRef, dataForThisDoc) => {
			if (!(await touchesCalendar(dataForThisDoc.coachID))) return
			try {
				const eventID = await resolveEventID(lessonRef, dataForThisDoc)
				const eventLocation = await resolveEventLocation(dataForThisDoc.schoolID)
				const savedEventID = await editCalendarEvent({
					...dataForThisDoc,
					lessonID: lessonRef.id,
					eventID,
					eventTitle: lessonEventTitle(dataForThisDoc),
					eventLocation,
				})
				if (savedEventID && savedEventID !== dataForThisDoc.eventID) {
					updateDoc(lessonRef, { eventID: savedEventID }).catch(error =>
						console.error("eventID della lezione non salvato:", error),
					)
				}
				console.info(`Evento ${savedEventID} aggiornato`)
			} catch (err) {
				queueCalendarFailure(err, { type: "lesson", lessonID: lessonRef.id })
				notifyCalendarError(
					err,
					"Lezione modificata, ma l'evento su Google Calendar non è aggiornato",
					dataForThisDoc.coachID,
				)
			}
		}
		// --- Singola lezione ---
		if (!editSeries || !recurrencyID) {
			// L'evento su Calendar non ha bisogno di niente che nasca dalla
			// scrittura su Firestore: parte in parallelo, così si aggiorna
			// anche quando la conferma del server tarda o non arriva.
			updateOneLessonCalendar(lessonRef, resolvedLessonData) // async, non blocca
			const acked = await ackOrPendingSync(
				updateOneLessonFirestore(lessonRef, resolvedLessonData),
				{
					pendingMessage: "Modifica salvata: si sincronizzerà appena torna la rete",
					failureMessage: "La modifica della lezione non è stata salvata",
				},
			)
			return { success: true, updated: 1, pendingSync: !acked }
		}

		// --- Serie di lezioni ---
		const selectedStartDate = lessonStart.toDate()
		const selectedEndDate = lessonEnd.toDate()
		const dayStart = new Date(selectedStartDate)
		dayStart.setHours(0, 0, 0, 0)

		const q = query(
			collection(firestoreDB, "lessons"),
			where("schoolID", "==", lessonData.schoolID),
			where("recurrencyID", "==", recurrencyID),
			where("lessonStart", ">=", Timestamp.fromDate(dayStart)),
		)
		const snapshot = await getDocs(q)

		// Aggiorno ogni lezione mantenendo il proprio eventID. Le lezioni
		// annullate restano come sono: sono andate così, e la serie continua
		// senza di loro.
		const seriesDocs = snapshot.docs.filter(
			docSnap => docSnap.id === lessonID || !isCancelledLesson(docSnap.data()),
		)
		const updates = seriesDocs.map(docSnap => {
			const data = docSnap.data()
			const oldStart = data.lessonStart.toDate()
			const offsetWeeks = Math.round(
				(oldStart.getTime() - selectedStartDate.getTime()) / (1000 * 60 * 60 * 24 * 7),
			)

			const newS = new Date(selectedStartDate.getTime())
			newS.setDate(newS.getDate() + offsetWeeks * 7)
			const newE = new Date(selectedEndDate.getTime())
			newE.setDate(newE.getDate() + offsetWeeks * 7)

			// Chi c'era e chi mancava, e chi ha sostituito chi, sono di ogni
			// lezione: la modifica della serie cambia gli allievi, non copia
			// su tutte le assenze e i sostituti di quella che si è aperta.
			const savedPartecipants = data.partecipants || []
			const regulars = regularPartecipants(resolvedLessonData.partecipants).map(player => {
				const saved = savedPartecipants.find(
					savedPlayer =>
						!isSubstitute(savedPlayer) &&
						substituteKey(savedPlayer) === substituteKey(player),
				)
				// e così un'assenza recuperata altrove, che è di quella lezione
				const { recoveredElsewhere, ...rest } = player
				return {
					...rest,
					isPresent: saved?.isPresent ?? true,
					...(saved?.recoveredElsewhere === true &&
						saved?.isPresent === false && { recoveredElsewhere: true }),
				}
			})
			// la lezione aperta invece tiene quelle scritte nel modulo
			const isOpenedLesson = docSnap.id === lessonID
			const perLessonData = {
				...resolvedLessonData,
				partecipants: isOpenedLesson
					? resolvedLessonData.partecipants
					: [
							...regulars,
							...keptSubstitutes(lessonSubstitutes(savedPartecipants), regulars),
						],
				lessonStart: Timestamp.fromDate(newS),
				lessonEnd: Timestamp.fromDate(newE),
				eventID: data.eventID ?? undefined, // mantieni l’eventID specifico
				coachID: lessonData.coachID ?? data.coachID, // se coach modificato, usa nuovo
			}

			updateOneLessonCalendar(doc(firestoreDB, "lessons", docSnap.id), perLessonData) // async
			return updateOneLessonFirestore(doc(firestoreDB, "lessons", docSnap.id), perLessonData)
		})

		const acked = await ackOrPendingSync(Promise.all(updates), {
			pendingMessage: "Modifiche salvate: si sincronizzeranno appena torna la rete",
			failureMessage: "La modifica delle lezioni non è stata salvata",
		})

		return { success: true, updated: seriesDocs.length, recurrencyID, pendingSync: !acked }
	} catch (error) {
		console.error("Errore editLesson:", error)
		throw error
	}
}

// Segna presente o assente l'allievo al posto `index` della lezione. Il posto
// conta più dell'ID: c'è chi l'ID non ce l'ha, e con l'ID soltanto un
// allievo senza avrebbe cambiato tutti gli altri senza. Se nel frattempo la
// lezione è cambiata e a quel posto c'è qualcun altro, si cerca per ID.
export const setPartecipantPresence = (partecipants, index, playerID, isPresent) => {
	const atIndex = partecipants[index]
	const target =
		atIndex && (atIndex.playerID ?? "") === (playerID ?? "")
			? index
			: playerID
			? partecipants.findIndex(p => p.playerID === playerID)
			: -1
	if (target === -1) return null
	// chi torna presente non ha più un'assenza, né recuperata altrove
	return withoutStaleRecoveredElsewhere(
		partecipants.map((p, i) => (i === target ? { ...p, isPresent } : p)),
	)
}

export const editParticipantPresence = async (lessonID, { playerID, index }, isPresent) => {
	const lessonRef = doc(firestoreDB, "lessons", lessonID)

	const write = (async () => {
		// La presenza che si vede — nella lezione e nel riepilogo del
		// profilo — è quella scritta sulla lezione: si parte da lì. Prima
		// si partiva dal documento dell'allievo con `updateDoc`, che non
		// esiste per le lezioni vecchie o salvate a metà: falliva, e la
		// lezione non veniva mai toccata.
		const lessonSnap = await getDoc(lessonRef)
		if (!lessonSnap.exists()) throw new Error("Lezione non trovata")

		const partecipants = setPartecipantPresence(
			lessonSnap.data().partecipants || [],
			index,
			playerID,
			isPresent,
		)
		if (!partecipants) throw new Error("Allievo non trovato nella lezione")

		const writes = [
			updateDoc(lessonRef, {
				partecipants,
				tracksRecovery: lessonTracksRecovery(partecipants),
				...lessonStamp(),
			}),
		]
		// Il documento dell'allievo si crea se manca, invece di pretendere
		// che ci sia. Chi non ha ID non ce l'ha e non può averlo.
		if (playerID) {
			writes.push(
				setDoc(
					doc(firestoreDB, "lessons", lessonID, "participants", playerID),
					{ playerID, lessonID, isPresent, updatedAt: Timestamp.now() },
					{ merge: true },
				),
			)
		}
		await Promise.all(writes)
	})()

	try {
		await ackOrPendingSync(write, {
			pendingMessage: "Presenza salvata: si sincronizzerà appena torna la rete",
			failureMessage: "La presenza non è stata salvata",
		})
		return { success: true }
	} catch (error) {
		console.error("Errore editParticipantPresence:", error)
		throw error
	}
}

// Un'assenza da abbonato recuperata fuori dall'app — di solito con un altro
// maestro del circolo — o rimessa da recuperare. L'assenza resta: cambia
// solo il segno `recoveredElsewhere` su quell'allievo, nella copia sulla
// lezione, che è quella da cui si contano i recuperi. Come la presenza, si
// parte dalla lezione com'è adesso (una lettura), per non riscrivere sopra
// a quello che un collega ha cambiato nel frattempo; e tocca solo i campi
// che le regole lasciano scrivere anche a chi segna le presenze.
export const editRecoveredElsewhere = async (lessonID, { playerID, index }, recovered) => {
	const lessonRef = doc(firestoreDB, "lessons", lessonID)

	const write = (async () => {
		const lessonSnap = await getDoc(lessonRef)
		if (!lessonSnap.exists()) throw new Error("Lezione non trovata")

		const partecipants = setRecoveredElsewhere(
			lessonSnap.data().partecipants || [],
			{ index, playerID },
			recovered,
		)
		if (!partecipants) throw new Error("Assenza non trovata nella lezione")

		await updateDoc(lessonRef, {
			partecipants,
			tracksRecovery: lessonTracksRecovery(partecipants),
			...lessonStamp(),
		})
	})()

	try {
		await ackOrPendingSync(write, {
			pendingMessage: "Assenza salvata: si sincronizzerà appena torna la rete",
			failureMessage: "L'assenza non è stata salvata",
		})
		return { success: true }
	} catch (error) {
		console.error("Errore editRecoveredElsewhere:", error)
		throw error
	}
}

// Il contenuto tecnico della lezione (src/utils/lessonContent.utils.js). Si
// scrive solo da qui: `addLesson` ed `editLesson` non lo toccano, così una
// serie modificata "anche le prossime" non copia il contenuto di una
// lezione sulle altre, e una lezione duplicata nasce senza. Un contenuto
// vuoto toglie il campo, invece di lasciarne uno pieno di niente. Non va
// sul calendario: niente da rifare lì.
export const editLessonContent = async (lessonID, content) => {
	const normalized = normalizeLessonContent(content)
	const write = updateDoc(doc(firestoreDB, "lessons", lessonID), {
		lessonContent: isLessonContentEmpty(normalized) ? deleteField() : normalized,
		...lessonStamp(),
	})

	try {
		return await ackOrPendingSync(write, {
			pendingMessage: "Programma salvato: si sincronizzerà appena torna la rete",
			failureMessage: "Il programma della lezione non è stato salvato",
		})
	} catch (error) {
		console.error("Errore editLessonContent:", error)
		throw error
	}
}

// ── IL QUADERNO ────────────────────────────────────────────────────
// Le lezioni e gli esercizi da riusare (src/utils/notebook.utils.js). Sono
// del maestro, non di un circolo: si ascoltano tutti quelli che ha scritto,
// e le regole lasciano leggere e scrivere solo quelli.
export const subscribeToNotebook = (coachID, onEntries, onError) =>
	onSnapshot(
		query(collection(firestoreDB, "notebook"), where("coachID", "==", coachID)),
		snapshot => onEntries(snapshot.docs.map(entry => normalizeNotebookEntry(entry.id, entry.data()))),
		error => {
			console.error("Quaderno non letto:", error)
			onError?.(error)
		},
	)

// Quello che i colleghi condividono in un circolo, senza le proprie (che
// arrivano già dall'ascolto di sopra: lette due volte si pagherebbero due
// volte). Le regole lasciano leggere una voce condivisa a chi sta in uno
// dei suoi circoli, e la query filtra sul circolo: ogni voce che torna è una
// che si può leggere. Vuole un indice (firestore.indexes.json).
export const subscribeToSharedNotebook = (schoolID, coachID, onEntries, onError) =>
	onSnapshot(
		query(
			collection(firestoreDB, "notebook"),
			where("sharedSchoolIDs", "array-contains", schoolID),
			where("coachID", "!=", coachID),
		),
		snapshot => onEntries(snapshot.docs.map(entry => normalizeNotebookEntry(entry.id, entry.data()))),
		error => {
			console.error("Quaderno dei colleghi non letto:", error)
			onError?.(error)
		},
	)

// Una voce nuova. L'ID si sa subito: chi la salva da una lezione se la
// ricorda come provenienza anche prima che il server confermi. Torna anche
// l'id della sua prima variante, che la lezione si ricorda allo stesso modo.
export const addNotebookEntry = async (coachID, entry) => {
	const entryRef = doc(collection(firestoreDB, "notebook"))
	const now = Timestamp.now()
	const fields = notebookEntryFields(entry)
	const write = setDoc(entryRef, {
		...fields,
		coachID,
		sharedSchoolIDs: [],
		...(entry.copiedFrom ? { copiedFrom: entry.copiedFrom } : {}),
		usesCount: 0,
		lastUsedAt: null,
		createdAt: now,
		updatedAt: now,
	})
	await ackOrPendingSync(write, {
		pendingMessage: "Salvato nel quaderno: si sincronizzerà appena torna la rete",
		failureMessage: "Non è stato salvato nel quaderno",
	})
	return { entryID: entryRef.id, variantID: fields.variantID }
}

// Si scrive la voce intera, con tutte le sue varianti: sono poche, e così
// una variante non resta mai a metà fra due salvataggi
export const editNotebookEntry = async (entryID, entry) => {
	const write = updateDoc(doc(firestoreDB, "notebook", entryID), {
		...notebookEntryFields(entry),
		updatedAt: Timestamp.now(),
	})
	await ackOrPendingSync(write, {
		pendingMessage: "Quaderno aggiornato: si sincronizzerà appena torna la rete",
		failureMessage: "Il quaderno non è stato aggiornato",
	})
}

// Le lezioni che l'hanno usata restano come sono: il contenuto l'avevano
// copiato.
export const deleteNotebookEntry = async entryID => {
	await ackOrPendingSync(deleteDoc(doc(firestoreDB, "notebook", entryID)), {
		pendingMessage: "Tolto dal quaderno: si sincronizzerà appena torna la rete",
		failureMessage: "Non è stato tolto dal quaderno",
	})
}

// Condividere coi colleghi di un circolo, o smettere. Vale per il circolo
// che si sta guardando: chi insegna in due posti sceglie per ognuno.
export const setNotebookEntryShared = async (entryID, schoolID, shared) => {
	const write = updateDoc(doc(firestoreDB, "notebook", entryID), {
		sharedSchoolIDs: shared ? arrayUnion(schoolID) : arrayRemove(schoolID),
	})
	await ackOrPendingSync(write, {
		pendingMessage: "Si sincronizzerà appena torna la rete",
		failureMessage: shared ? "Non è stato condiviso" : "È ancora condiviso",
	})
}

// Una variante com'è scritta: il livello, cosa cambia e, per una lezione il
// programma, per un esercizio il testo e lo schema (`null` se non ce l'ha)
const notebookVariantFields = (kind, variant) => ({
	variantID: variant.variantID || newVariantID(),
	level: variant.level ?? null,
	note: (variant.note ?? "").trim().slice(0, NOTEBOOK_NOTE_MAX),
	...(kind === NOTEBOOK_EXERCISE
		? { text: (variant.text ?? "").trim(), schema: normalizeSchema(variant.schema) }
		: { content: notebookLessonContent(variant.content) }),
})

// Quello che di una voce si scrive: il nome, per un esercizio la fase, e le
// varianti. La prima sta al primo livello del documento, come prima delle
// varianti, le altre in `variants`. Chi passa una voce senza varianti (il
// salvataggio dal programma) ne scrive una sola, coi campi di sempre.
const notebookEntryFields = entry => {
	const kind = entry.kind === NOTEBOOK_EXERCISE ? NOTEBOOK_EXERCISE : NOTEBOOK_LESSON
	const variants =
		Array.isArray(entry.variants) && entry.variants.length > 0 ? entry.variants : [entry]
	const [first, ...rest] = variants.map(variant => notebookVariantFields(kind, variant))
	return {
		kind,
		title: entry.title.trim(),
		...(kind === NOTEBOOK_EXERCISE ? { phase: entry.phase ?? null } : {}),
		...first,
		variants: rest,
	}
}

// Quante volte una voce è stata usata, e l'ultima: dopo che la lezione è
// salvata, e senza fermarla se non riesce. Una voce tolta nel frattempo non
// ha più niente da contare. Di un collega si conta a parte, e solo quello:
// le regole gli lasciano scrivere `schoolUses`, di uno, e nient'altro.
export const recordNotebookUses = (entries, lessonStart) =>
	Promise.all(
		entries.map(entry =>
			updateDoc(
				doc(firestoreDB, "notebook", entry.entryID),
				isMine(entry)
					? {
							usesCount: increment(1),
							lastUsedAt: laterUse(entry.lastUsedAt, lessonStart) ?? null,
						}
					: { schoolUses: increment(1) },
			).catch(error => console.warn(`Uso di ${entry.entryID} non contato:`, error)),
		),
	)

// Le lezioni del circolo che contano per i recuperi, dall'inizio della
// stagione: quelle con un'assenza da abbonato o un recupero, e solo quelle.
// Sono poche rispetto a tutte, e bastano a dire chi ha ancora lezioni da
// recuperare — qualunque mese si stia guardando. Si ascoltano, così una
// presenza segnata adesso si vede subito.
export const listenSeasonRecoveryLessons = (schoolID, seasonStart, onLessons) =>
	onSnapshot(
		query(
			collection(firestoreDB, "lessons"),
			where("schoolID", "==", schoolID),
			where("tracksRecovery", "==", true),
			where("lessonStart", ">=", Timestamp.fromDate(seasonStart)),
		),
		snapshot =>
			onLessons(snapshot.docs.map(docSnap => ({ lessonID: docSnap.id, ...docSnap.data() }))),
		error => console.error("Assenze della stagione non lette:", error),
	)

// Le lezioni di un allievo che contano per i recuperi, dall'inizio della
// stagione: le sue con `tracksRecovery`, non tutte. Sono una manciata anche a
// primavera, e bastano a dire nella sua scheda quante ne ha saltate e
// recuperate. Indice: schoolID + partecipantIDs (array) + tracksRecovery +
// lessonStart.
export const getPlayerSeasonRecoveryLessons = async (schoolID, playerID, seasonStart) => {
	if (!schoolID || !playerID) return []

	const snapshot = await getDocs(
		query(
			collection(firestoreDB, "lessons"),
			where("schoolID", "==", schoolID),
			where("partecipantIDs", "array-contains", playerID),
			where("tracksRecovery", "==", true),
			where("lessonStart", ">=", Timestamp.fromDate(seasonStart)),
		),
	)

	return snapshot.docs.map(docSnap => ({ lessonID: docSnap.id, ...docSnap.data() }))
}

// `array-contains-any` accetta al massimo 30 valori
const ANY_LIMIT = 30

// Quanti recuperi del corso `courseType` restano a questi allievi nella
// stagione della data `date`, `{ [playerID]: numero }`. Si leggono solo le
// loro lezioni che contano per i recuperi (`tracksRecovery`): le altre non
// cambiano il conto, e sono quasi tutte. E una query sola per tutti gli
// allievi della lezione, non una a testa. Indice: schoolID + partecipantIDs
// (array) + tracksRecovery + lessonStart, lo stesso della scheda.
export const getRecoveriesLeft = async (schoolID, playerIDs, date, excludeLessonID, courseType) => {
	const ids = [...new Set((playerIDs || []).filter(Boolean))]
	if (!schoolID || ids.length === 0 || !isSubscriptionCourse(courseType))
		return Object.fromEntries(ids.map(playerID => [playerID, 0]))

	const { start, end } = getSeasonBounds(date)
	const chunks = []
	for (let i = 0; i < ids.length; i += ANY_LIMIT) chunks.push(ids.slice(i, i + ANY_LIMIT))
	const snapshots = await Promise.all(
		chunks.map(chunk =>
			getDocs(
				query(
					collection(firestoreDB, "lessons"),
					where("schoolID", "==", schoolID),
					where("partecipantIDs", "array-contains-any", chunk),
					where("tracksRecovery", "==", true),
					where("lessonStart", ">=", Timestamp.fromDate(start)),
					where("lessonStart", "<", Timestamp.fromDate(end)),
				),
			),
		),
	)

	// una lezione con allievi di due gruppi arriva due volte: conta una
	const lessons = new Map()
	snapshots.forEach(snapshot =>
		snapshot.docs.forEach(docSnap =>
			lessons.set(docSnap.id, { lessonID: docSnap.id, ...docSnap.data() }),
		),
	)

	return Object.fromEntries(
		ids.map(playerID => [
			playerID,
			recoveriesLeft([...lessons.values()], playerID, excludeLessonID, courseType),
		]),
	)
}

// Mette, cambia o toglie (`substitute` null) il sostituto di `absent` in una
// lezione già salvata. Non passa da editLesson: lì si riscrive la lezione
// intera e il suo evento su Calendar, qui cambia un allievo solo.
export const setLessonSubstitute = async (lessonID, absent, substitute) => {
	const lessonRef = doc(firestoreDB, "lessons", lessonID)
	const participantsRef = collection(firestoreDB, "lessons", lessonID, "participants")

	const write = (async () => {
		const lessonSnap = await getDoc(lessonRef)
		if (!lessonSnap.exists()) throw new Error("Lezione non trovata")

		const lesson = lessonSnap.data()
		const previous = substituteOf(lesson.partecipants, absent)
		const partecipants = withSubstitute(lesson.partecipants || [], absent, substitute)

		const writes = [
			updateDoc(lessonRef, {
				partecipants,
				partecipantsCount: partecipants.length,
				partecipantIDs: getPartecipantIDs(partecipants),
				tracksRecovery: lessonTracksRecovery(partecipants),
				...lessonStamp(),
			}),
		]

		// i pagamenti di chi entra o esce da questa lezione
		const savedPayments = await getDocs(
			query(
				collection(firestoreDB, "payments"),
				where("schoolID", "==", lesson.schoolID),
				where("lessonID", "==", lessonID),
			),
		)
		const paymentsOf = playerID =>
			savedPayments.docs.filter(paymentDoc => paymentDoc.data().playerID === playerID)

		if (previous?.playerID && previous.playerID !== substitute?.playerID) {
			writes.push(deleteDoc(doc(participantsRef, previous.playerID)))
			paymentsOf(previous.playerID).forEach(paymentDoc => writes.push(deleteDoc(paymentDoc.ref)))
		}

		if (substitute?.playerID) {
			const [savedPayment, ...duplicates] = paymentsOf(substitute.playerID)
			duplicates.forEach(paymentDoc => writes.push(deleteDoc(paymentDoc.ref)))

			let paymentRef = null
			if (hasLessonPayment(substitute)) {
				paymentRef = savedPayment?.ref ?? doc(collection(firestoreDB, "payments"))
				writes.push(
					setDoc(
						paymentRef,
						{
							paymentDate: lesson.lessonStart,
							paymentMethod: substitute.paymentMethod,
							paymentAmount: substitute.paymentAmount ?? 0,
							// chi l'ha incassato: il circolo, o il maestro per lui
							...(substitute.paidTo ? { paidTo: substitute.paidTo } : {}),
							playerID: substitute.playerID,
							lessonID,
							coachID: lesson.coachID ?? "",
							schoolID: lesson.schoolID,
							...(savedPayment ? {} : { createdAt: Timestamp.now() }),
							updatedAt: Timestamp.now(),
						},
						{ merge: true },
					),
				)
			} else if (savedPayment) {
				writes.push(deleteDoc(savedPayment.ref))
			}

			writes.push(
				setDoc(
					doc(participantsRef, substitute.playerID),
					{
						playerID: substitute.playerID,
						lessonID,
						paymentID: paymentRef?.id ?? "",
						paymentMethod: substitute.paymentMethod ?? "",
						isPresent: true,
						subscriptionID: "",
						substituteFor: substituteKey(absent),
						updatedAt: Timestamp.now(),
					},
					{ merge: true },
				),
			)
		}

		await Promise.all(writes)
	})()

	try {
		await ackOrPendingSync(write, {
			pendingMessage: "Sostituto salvato: si sincronizzerà appena torna la rete",
			failureMessage: "Il sostituto non è stato salvato",
		})
		return { success: true }
	} catch (error) {
		console.error("Errore setLessonSubstitute:", error)
		throw error
	}
}

// ── ANNULLARE UNA LEZIONE ──────────────────────────────────────────
// Un corso in abbonamento che salta non si elimina: resta salvato con
// `cancelled: true` e gli abbonati assenti, così ognuno ha la sua lezione da
// recuperare (vedi src/utils/cancellation.utils.js). Gli allievi com'erano
// prima restano scritti sulla lezione, in `partecipantsBeforeCancel`: è da
// lì che il ripristino li rimette com'erano.

// Annulla una lezione sola: per una ricorrente la serie va avanti. Le note,
// se si danno, prendono il posto di quelle della lezione: è lì che si
// scrive perché è saltata.
export const cancelLesson = async (lessonData, { lessonNotes } = {}) => {
	const { lessonID, schoolID } = lessonData
	const lessonRef = doc(firestoreDB, "lessons", lessonID)
	const participantsRef = collection(firestoreDB, "lessons", lessonID, "participants")

	// prima dell'annullamento: dopo sulla lezione non c'è più
	const eventID = await resolveEventID(lessonRef, lessonData)

	const write = (async () => {
		const lessonSnap = await getDoc(lessonRef)
		if (!lessonSnap.exists()) throw new Error("Lezione non trovata")
		const lesson = lessonSnap.data()
		if (isCancelledLesson(lesson)) return

		const before = lesson.partecipants || []
		const partecipants = cancelledPartecipants(before)

		// I pagamenti di chi pagava la volta: la lezione non si è fatta, e
		// non c'è niente da incassare.
		const savedPayments = await getDocs(
			query(
				collection(firestoreDB, "payments"),
				where("schoolID", "==", lesson.schoolID),
				where("lessonID", "==", lessonID),
			),
		)

		const batch = writeBatch(firestoreDB)
		batch.update(lessonRef, {
			cancelled: true,
			cancelledAt: Timestamp.now(),
			partecipantsBeforeCancel: before,
			partecipants,
			// Sempre, anche senza abbonati: il profilo cerca le assenze
			// della stagione tra le lezioni che lo dicono.
			tracksRecovery: true,
			lessonNotes: lessonNotes ?? lesson.lessonNotes ?? "",
			// l'evento si cancella, e il ripristino ne fa uno nuovo
			eventID: "",
			...lessonStamp(),
		})
		savedPayments.forEach(paymentDoc => batch.delete(paymentDoc.ref))
		partecipants
			.filter(player => player.playerID)
			.forEach(player =>
				batch.set(
					doc(participantsRef, player.playerID),
					{
						playerID: player.playerID,
						lessonID,
						isPresent: player.isPresent ?? true,
						paymentMethod: player.paymentMethod ?? "",
						paymentID: "",
						updatedAt: Timestamp.now(),
					},
					{ merge: true },
				),
			)
		await batch.commit()
	})()

	try {
		const acked = await ackOrPendingSync(write, {
			pendingMessage: "Lezione annullata: si sincronizzerà appena torna la rete",
			failureMessage: "La lezione non è stata annullata",
		})

		// Come eliminando senza rete: i pagamenti che il telefono non aveva
		// visto si cercano sul server quando l'annullamento è arrivato.
		if (!acked) {
			const cleanup = write.then(
				() => deleteLeftoverPayments(lessonID, schoolID),
				() => null,
			)
			trackSync(cleanup)
			cleanup.catch(error =>
				console.error("Pagamenti rimasti alla lezione annullata:", error),
			)
		}
	} catch (error) {
		console.error("Errore cancelLesson:", error)
		throw error
	}

	// L'evento su Google Calendar si cancella: la lezione non c'è più. Senza
	// eventID è quello con l'ID ricavato dalla lezione (`resolveEventID`).
	;(async () => {
		if (!(await touchesCalendar(lessonData.coachID))) return
		try {
			await deleteCalendarEvent({ ...lessonData, eventID })
			console.info(`Evento ${eventID} eliminato`)
		} catch (err) {
			queueCalendarFailure(err, { type: "delete", coachID: lessonData.coachID, eventID })
			notifyCalendarError(
				err,
				"Lezione annullata, ma l'evento è ancora su Google Calendar",
				lessonData.coachID,
			)
		}
	})()

	return { success: true }
}

// La lezione di recupero fatta con "Annulla e riprogramma": la lezione
// annullata se la ricorda, così ripristinandola si può chiedere che fine
// debba fare.
export const linkRescheduledLesson = async (cancelledLessonID, rescheduledLessonID) => {
	const write = updateDoc(doc(firestoreDB, "lessons", cancelledLessonID), {
		rescheduledLessonID,
		...lessonStamp(),
	})
	await ackOrPendingSync(write, {
		failureMessage: "La lezione di recupero non è stata collegata a quella annullata",
	})
}

// Ripristina una lezione annullata: allievi e presenze tornano com'erano
// prima dell'annullamento, chi pagava la volta ritrova il suo pagamento e
// l'evento su Google Calendar si ricrea. `deleteRescheduled` elimina anche
// la lezione di recupero, se ce n'era una: torna la data che si elimina, così
// chi chiama può rileggerla.
export const restoreLesson = async (
	lessonData,
	{ deleteRescheduled = false, lessonTypeName } = {},
) => {
	const { lessonID } = lessonData
	const lessonRef = doc(firestoreDB, "lessons", lessonID)
	const participantsRef = collection(firestoreDB, "lessons", lessonID, "participants")

	let restored = null

	const write = (async () => {
		const lessonSnap = await getDoc(lessonRef)
		if (!lessonSnap.exists()) throw new Error("Lezione non trovata")
		const lesson = lessonSnap.data()
		if (!isCancelledLesson(lesson)) return

		const partecipants = lesson.partecipantsBeforeCancel ?? lesson.partecipants ?? []

		const savedPayments = await getDocs(
			query(
				collection(firestoreDB, "payments"),
				where("schoolID", "==", lesson.schoolID),
				where("lessonID", "==", lessonID),
			),
		)
		const { paymentByPlayer, obsoletePayments } = reconcileLessonPayments({
			savedPayments: savedPayments.docs.map(paymentDoc => ({
				playerID: paymentDoc.data().playerID,
				ref: paymentDoc.ref,
			})),
			partecipants,
			newPaymentRef: () => doc(collection(firestoreDB, "payments")),
		})

		const batch = writeBatch(firestoreDB)
		batch.update(lessonRef, {
			cancelled: false,
			cancelledAt: deleteField(),
			partecipantsBeforeCancel: deleteField(),
			rescheduledLessonID: deleteField(),
			partecipants,
			partecipantsCount: partecipants.length,
			partecipantIDs: getPartecipantIDs(partecipants),
			tracksRecovery: lessonTracksRecovery(partecipants),
			...lessonStamp(),
		})
		obsoletePayments.forEach(paymentRef => batch.delete(paymentRef))
		partecipants
			.filter(player => player.playerID)
			.forEach(player => {
				const payment = paymentByPlayer.get(player.playerID)
				if (payment) {
					batch.set(
						payment.ref,
						{
							paymentDate: lesson.lessonStart,
							paymentMethod: player.paymentMethod,
							paymentAmount: player.paymentAmount ?? 0,
							...(player.paidTo ? { paidTo: player.paidTo } : {}),
							playerID: player.playerID,
							lessonID,
							coachID: lesson.coachID ?? "",
							schoolID: lesson.schoolID,
							...(payment.isNew ? { createdAt: Timestamp.now() } : {}),
							updatedAt: Timestamp.now(),
						},
						{ merge: true },
					)
				}
				batch.set(
					doc(participantsRef, player.playerID),
					{
						playerID: player.playerID,
						lessonID,
						isPresent: player.isPresent ?? true,
						paymentMethod: player.paymentMethod ?? "",
						paymentID: payment?.ref.id ?? "",
						updatedAt: Timestamp.now(),
					},
					{ merge: true },
				)
			})
		await batch.commit()
		restored = { ...lesson, partecipants }
	})()

	try {
		await ackOrPendingSync(write, {
			pendingMessage: "Lezione ripristinata: si sincronizzerà appena torna la rete",
			failureMessage: "La lezione non è stata ripristinata",
		})
	} catch (error) {
		console.error("Errore restoreLesson:", error)
		throw error
	}

	// La lezione di recupero, se si è scelto di toglierla: una lezione come
	// le altre, che si elimina come le altre.
	let deletedRescheduledDate = null
	const rescheduledLessonID = lessonData.rescheduledLessonID
	if (deleteRescheduled && rescheduledLessonID) {
		try {
			const rescheduledSnap = await getDoc(doc(firestoreDB, "lessons", rescheduledLessonID))
			if (rescheduledSnap.exists()) {
				const rescheduled = { lessonID: rescheduledLessonID, ...rescheduledSnap.data() }
				await deleteLesson(rescheduled)
				deletedRescheduledDate = rescheduled.lessonStart.toDate().toISOString().split("T")[0]
			}
		} catch (error) {
			console.error("Lezione di recupero non eliminata:", error)
			toast.error("Lezione ripristinata, ma la lezione di recupero è ancora lì", {
				toastId: "rescheduled-lesson-not-deleted",
				position: "top-center",
			})
		}
	}

	// L'evento su Google Calendar si ricrea, con gli allievi di adesso. Ha
	// l'ID ricavato dalla lezione, come quello tolto annullandola: Google lo
	// rimette in calendario invece di farne un altro.
	;(async () => {
		if (!(await touchesCalendar(lessonData.coachID))) return
		try {
			const eventLocation = await resolveEventLocation(lessonData.schoolID)
			const lesson = { ...lessonData, ...(restored ?? {}), lessonTypeName }
			const eventID = await createCalendarEvent({
				...lesson,
				lessonID,
				eventTitle: lessonEventTitle(lesson),
				eventLocation,
			})
			if (eventID) {
				await updateDoc(lessonRef, { eventID })
				console.info(`Evento Google Calendar ${eventID} ricreato`)
			}
		} catch (err) {
			queueCalendarFailure(err, { type: "lesson", lessonID })
			notifyCalendarError(
				err,
				"Lezione ripristinata, ma non sincronizzata su Google Calendar",
				lessonData.coachID,
			)
		}
	})()

	return { success: true, deletedRescheduledDate }
}

// Elimina una lezione
export const deleteLesson = async (lessonData, deleteSeries = false) => {
	try {
		const { lessonID, recurrencyID, lessonStart } = lessonData

		// Le lezioni di una serie si cercano: senza rete si troverebbero solo
		// quelle che il telefono ha già visto, e la serie resterebbe eliminata
		// a metà. Una lezione sola invece si elimina anche senza rete (a bordo
		// campo capita: pioggia), e quello che non si trova lo si pulisce dopo.
		if (deleteSeries && recurrencyID) {
			requireNetwork(
				"Per eliminare tutta la serie serve la rete: senza, si può eliminare solo questa lezione",
			)
		}

		// --- Funzione interna per cancellare una singola lezione su Firestore ---
		// Aspetta le letture, ma non la conferma del server: torna la promise
		// del commit, così chi chiama decide quanto aspettarla.
		const deleteOneLessonFirestore = async (lessonRef, schoolID) => {
			// rimuovo partecipanti
			const participantsSnap = await getDocs(collection(lessonRef, "participants"))
			const batch = writeBatch(firestoreDB)
			participantsSnap.forEach(p => batch.delete(p.ref))

			// rimuovo pagamenti
			const paymentsQ = query(
				collection(firestoreDB, "payments"),
				where("schoolID", "==", schoolID),
				where("lessonID", "==", lessonRef.id),
			)
			const paymentsSnap = await getDocs(paymentsQ)
			paymentsSnap.forEach(pay => batch.delete(pay.ref))

			// cancello la lezione
			batch.delete(lessonRef)
			return { commit: batch.commit() }
		}

		// --- Funzione interna per cancellare evento su Calendar ---
		// Senza eventID sulla lezione si toglie quello con l'ID ricavato dalla
		// lezione: se non c'è, Google risponde che non c'è, e va bene così.
		const deleteOneLessonCalendar = async (dataForThisDoc, lessonID, savedEventID) => {
			// l'evento di una lezione annullata è già stato tolto
			if (isCancelledLesson(dataForThisDoc)) return
			if (!(await touchesCalendar(dataForThisDoc.coachID))) return
			const eventID = savedEventID || calendarEventID(lessonID)
			try {
				await deleteCalendarEvent({ ...dataForThisDoc, eventID })
				console.info(`Evento ${eventID} eliminato`)
			} catch (err) {
				queueCalendarFailure(err, {
					type: "delete",
					coachID: dataForThisDoc.coachID,
					eventID,
				})
				notifyCalendarError(
					err,
					"Lezione eliminata, ma l'evento è ancora su Google Calendar",
					dataForThisDoc.coachID,
				)
			}
		}

		// --- Singola lezione ---
		if (!deleteSeries || !recurrencyID) {
			const lessonRef = doc(firestoreDB, "lessons", lessonID)
			// prima della cancellazione: dopo il documento non c'è più
			const eventID = await resolveEventID(lessonRef, lessonData)
			const { commit } = await deleteOneLessonFirestore(lessonRef, lessonData.schoolID)
			const acked = await ackOrPendingSync(commit, {
				pendingMessage: "Lezione eliminata: si sincronizzerà appena torna la rete",
				failureMessage: "La lezione non è stata eliminata",
			})

			// Senza rete allievi e pagamenti si sono cercati sul telefono, che
			// può non averli visti tutti: i pagamenti mancati si cercano sul
			// server quando la cancellazione arriva. Se l'app si chiude prima,
			// restano solo nell'esportazione dei dati — l'app li ignora, perché
			// i conti li fa sulle lezioni.
			if (!acked) {
				const cleanup = commit.then(
					() => deleteLeftoverPayments(lessonID, lessonData.schoolID),
					// se non è passata la cancellazione, lo dice già chi l'aspettava
					() => null,
				)
				trackSync(cleanup)
				cleanup.catch(error =>
					console.error("Pagamenti rimasti alla lezione eliminata:", error),
				)
			}

			deleteOneLessonCalendar(lessonData, lessonID, eventID) // async
			return { success: true, deleted: 1 }
		}

		// --- Serie di lezioni ---
		const dayStart = new Date(lessonStart.toDate())
		dayStart.setHours(0, 0, 0, 0)

		const q = query(
			collection(firestoreDB, "lessons"),
			where("schoolID", "==", lessonData.schoolID),
			where("recurrencyID", "==", recurrencyID),
			where("lessonStart", ">=", Timestamp.fromDate(dayStart)),
			orderBy("lessonStart", "asc"),
		)

		const snapshot = await getDocs(q)

		// Prima si preparano tutte le cancellazioni, poi se ne aspetta la
		// conferma del server tutta insieme, e non oltre `SERVER_ACK_TIMEOUT`:
		// con tante lezioni le conferme possono tardare parecchio, e intanto
		// le lezioni sono già sparite dalla copia locale.
		const commits = await Promise.all(
			snapshot.docs.map(async docSnap => {
				const { commit } = await deleteOneLessonFirestore(
					docSnap.ref,
					docSnap.data().schoolID,
				)
				return commit
			}),
		)

		await ackOrPendingSync(Promise.all(commits), {
			pendingMessage: "Lezioni eliminate: si sincronizzeranno appena torna la rete",
			failureMessage: "Le lezioni non sono state eliminate",
		})

		snapshot.docs.forEach(docSnap => {
			const data = docSnap.data()
			deleteOneLessonCalendar(data, docSnap.id, data.eventID) // async
		})

		return { success: true, deleted: snapshot.docs.length, recurrencyID }
	} catch (error) {
		console.error("Errore deleteLesson:", error)
		throw error
	}
}

// ── LEZIONI ELIMINATE ──────────────────────────────────────────────
// Il registro lo scrive il trigger `onLessonDeleted` in functions/index.js,
// mai l'app. Chi gestisce il circolo legge tutte le eliminazioni; gli altri
// solo quelle delle loro lezioni, e la query lo deve dire: le regole non
// filtrano, rifiutano. Le più recenti in cima, e non più di quante se ne
// leggono davvero.
const LESSON_DELETIONS_LIMIT = 300

export const subscribeToLessonDeletions = (schoolID, { coachID, all }, onDeletions, onError) => {
	const constraints = [where("schoolID", "==", schoolID)]
	if (!all) constraints.push(where("coachID", "==", coachID))

	return onSnapshot(
		query(
			collection(firestoreDB, "lessonDeletions"),
			...constraints,
			orderBy("deletedAt", "desc"),
			limit(LESSON_DELETIONS_LIMIT),
		),
		snapshot =>
			onDeletions(
				snapshot.docs.map(deletionDoc => ({ lessonID: deletionDoc.id, ...deletionDoc.data() })),
			),
		onError,
	)
}
